import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Dropdown, Form, Input, Modal, Radio, Row, Select, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  CompressOutlined,
  CopyOutlined,
  RobotOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  ExpandAltOutlined,
  FileTextOutlined,
  HighlightOutlined,
  LoadingOutlined,
  ReloadOutlined,
  RocketOutlined,
  SearchOutlined,
  SnippetsOutlined,
  SendOutlined,
  SettingOutlined,
  TagsOutlined,
  ThunderboltOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useLocation, useParams } from "react-router-dom";
import DescriptionEditor from "../../components/DescriptionEditor";
import WritingChatSidebar from "../../components/WritingChatSidebar";
import { useChatHistory } from "../../hooks/useChatHistory";
import useDraftAutosave from "../../hooks/useDraftAutosave";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";
import {
  replaceSelectionInHtml,
  selectionInsideElement,
  selectTextInElement,
  buildHighlightedHtml,
  englishVariantNote,
  EXCERPT_RULES,
} from "../../helper/writingSelection";

// ── Helpers ────────────────────────────────────────────────────────────────

const stripHtml = (value = "") => {
  const div = document.createElement("div");
  div.innerHTML = value;
  return div.innerText || div.textContent || "";
};

// Approximate syllable counter — close enough to track Flesch score direction across iterations
const countSyllables = (word) => {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return 0;
  if (w.length <= 3) return 1;
  const trimmed = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "").replace(/^y/, "");
  const matches = trimmed.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

// Flesch Reading Ease, computed client-side so we can iterate without a backend round trip.
// Returns 0-100 (same scale as textstat on the backend).
const computeFlesch = (text) => {
  const cleaned = (text || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return 0;
  const sentences = cleaned.split(/[.!?]+/).filter((s) => s.trim().length > 0);
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (!words.length) return 0;
  const sentenceCount = Math.max(1, sentences.length);
  const wordCount = words.length;
  const syllableCount = words.reduce((sum, w) => sum + countSyllables(w), 0);
  const fre = 206.835 - 1.015 * (wordCount / sentenceCount) - 84.6 * (syllableCount / wordCount);
  return Math.max(0, Math.min(100, fre));
};

// Fact-check accuracy — mirrors the backend formula so the banner recomputes
// live after Edit & Retry / Fix changes a claim's verdict. Unverified claims
// are excluded: "couldn't check" is not "false". Returns null when nothing
// received a verdict (banner hidden, claims still listed).
const computeAccuracy = (facts = []) => {
  const weights = { Supported: 1, "Partially Supported": 0.5, Contradicted: 0 };
  const scored = facts.filter((f) => weights[f.verdict] !== undefined);
  if (!scored.length) return null;
  const totalConf = scored.reduce((sum, f) => sum + Math.max(f.confidence || 0, 1), 0);
  const weighted = scored.reduce((sum, f) => sum + weights[f.verdict] * Math.max(f.confidence || 0, 1), 0);
  return Math.round((weighted / totalConf) * 100);
};

// Simple APA-style citation formatter for fact-check sources — originality.ai's
// dashboard offers export in APA/MLA/etc.; we don't have a proper bibliography
// engine, so this produces a "good enough" reference-list line per source.
const formatCitationAPA = (source) => {
  const title = source?.title?.trim() || source?.url || "Untitled source";
  const year = source?.date ? new Date(source.date).getFullYear() : null;
  const yearPart = year && !Number.isNaN(year) ? ` (${year}).` : ".";
  return `${title}${yearPart} Retrieved from ${source?.url || ""}`;
};

const extractJson = (value = "") => {
  const match = String(value).match(/\{[\s\S]*\}/);
  if (!match) return null;
  try { return JSON.parse(match[0]); } catch { return null; }
};

const normalizeForMatch = (s = "") =>
  s.toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim();

// Finds the <p> block in HTML content that contains (or best overlaps with) a claim.
// Exact-substring matching on HTML fails whenever the claim spans a <strong> tag boundary
// or has minor whitespace/quote differences — so this falls back to word-overlap scoring.
const findParagraphForClaim = (html, claimText) => {
  const paragraphs = html.match(/<p[^>]*>[\s\S]*?<\/p>/gi) || [html];
  const claimNorm = normalizeForMatch(stripHtml(claimText));
  if (!claimNorm) return null;

  let match = paragraphs.find((p) => normalizeForMatch(stripHtml(p)).includes(claimNorm));
  if (match) return match;

  const words = claimNorm.split(" ").filter(Boolean);
  if (words.length > 6) {
    const partial = words.slice(0, 6).join(" ");
    match = paragraphs.find((p) => normalizeForMatch(stripHtml(p)).includes(partial));
    if (match) return match;
  }

  const claimWordSet = new Set(words.filter((w) => w.length > 3));
  let bestPara = null;
  let bestRatio = 0;
  paragraphs.forEach((p) => {
    const pWords = normalizeForMatch(stripHtml(p)).split(" ");
    const overlap = pWords.filter((w) => claimWordSet.has(w)).length;
    const ratio = claimWordSet.size ? overlap / claimWordSet.size : 0;
    if (ratio > bestRatio) { bestRatio = ratio; bestPara = p; }
  });
  return bestRatio >= 0.5 ? bestPara : null;
};

// ── AI prompts ─────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();

const buildPostPrompt = ({ topic, keywords, maxWords }, currentContent = "") => `
You are a world-class LinkedIn ghostwriter and content strategist.

TODAY: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
YEAR: ${CURRENT_YEAR} — Write as a professional living and working in ${CURRENT_YEAR}.
- Use facts, statistics, and events from ${CURRENT_YEAR} or ${CURRENT_YEAR - 1} only.
- For AI/tech topics: name ACTUAL models, products, and companies with real figures. Never say "a leading AI company" — say "OpenAI", "DeepSeek", "Google Gemini" etc.
- Do NOT use data older than 2023 unless historically essential.

TOPIC: "${topic}"
${keywords ? `KEYWORDS TO WEAVE IN: ${keywords}` : ""}

LENGTH:
${maxWords
  ? `Target ${maxWords} words. Expand with specific examples, real-world implications, and data — no filler.`
  : "Aim for 150-350 words for a LinkedIn post. Concise but substantive."
}

━━━━━━━━━━━━━━━━━━━━━
WHAT MAKES THIS GREAT
━━━━━━━━━━━━━━━━━━━━━

HOOK (first 1-2 lines):
✓ Use a specific surprising stat: "73% of AI projects fail in production — not in development."
✓ Or a bold contrarian take that creates immediate tension
✗ NEVER start with: "In today's world..." / "I'm excited..." / "As we all know..." / "In the era of..."

BODY — go DEEP on ONE insight, not wide on five:
✓ Name real companies, real products, real people with real numbers
✓ Show BEFORE vs AFTER, or THEN vs NOW contrast
✓ Explain WHY this matters to the reader's career or business — make it personal
✗ No vague sentences like "AI is transforming industries" — say HOW, with what numbers

PROOF (specificity = credibility):
✗ "Studies show productivity improves with AI"
✓ "Microsoft's 2026 Work Trend Index: Copilot users finish tasks 29% faster"
✗ "DeepSeek is very capable"
✓ "DeepSeek-R2 matches GPT-4o on MMLU at 1/10th the inference cost"

CLOSING CTA (last line only):
✓ A genuine open question inviting the reader's opinion
✓ Example: "Is ${CURRENT_YEAR} the year enterprises finally trust AI agents in production?"

━━━━━━━━━━━━━━━━━━━━━
FORMAT
━━━━━━━━━━━━━━━━━━━━━
- Output clean HTML: <p> per paragraph, <strong> for key terms
- 1-2 sentences per paragraph, blank line between each (LinkedIn feed format)
- No hashtags unless requested, no markdown fences, no "Here's your post:" preamble
- Detect tone from topic (funny/bold/inspirational/serious) — default: sharp and professional

Output the content ONLY.
${currentContent ? `\nExisting content for context (improve upon or continue this):\n${stripHtml(currentContent)}` : ""}
`;

const buildAnalysisPrompt = (content, language) => `
Analyze this content and return ONLY valid JSON with this structure:
{
  "aiPct": number,
  "aiNote": "string",
  "readability": { "score": number, "label": "Very easy | Easy | Moderate | Hard | Very hard", "description": "string" },
  "facts": [{ "claim": "string", "reason": "string" }],
  "suggestions": ["string", "string", "string"],
  "humanized": "string"
}

Rules:
- aiPct must be 0 to 100.
- readability.score must be 0 to 10.
- If language is Arabic, return Arabic in text fields.
- humanized should keep the meaning but sound natural and professional.

Language: ${language || "en"}
Content:
${stripHtml(content)}`;

// Follow-up chat messages (typed once a draft already exists) are treated as
// free-text edit instructions applied to the current draft, not a fresh
// generation — "make it shorter", "add a stat about AI adoption", etc.
const buildEditPrompt = (instruction, currentContent) => `
You are editing an existing LinkedIn post.

INSTRUCTION FROM THE USER: "${instruction}"

TASK: Apply this instruction to the post below. Keep everything else about the post intact unless the instruction implies otherwise. Preserve facts, names, and numbers unless the instruction asks you to change them.

Return ONLY the updated post as clean HTML (<p> per paragraph, <strong> for key terms). No preamble, no explanation.

CURRENT POST:
${stripHtml(currentContent)}
`;

// Human-readable label posted into the chat thread when a quick action
// (Actions dropdown) is applied to the whole document — mirrors what the
// user "asked for" so the thread reads like a real conversation turn.
const actionLabel = (type, toneStr) => {
  if (type === "tone") return `Change tone to ${toneStr}`;
  const labels = {
    rewrite: "Rewrite this post",
    expand: "Expand this post",
    condense: "Condense this post",
    improve: "Improve the writing",
    simplify: "Simplify the language",
    grammar: "Fix grammar",
    professional: "Make it more professional",
    engaging: "Make it more engaging",
    humanize: "Humanize this post",
  };
  return labels[type] || "Rewrite this post";
};

// Normalisation ceilings so each gauge's arc fill reflects the value's
// magnitude within its usable range (originality.ai-style half-donut).
const GAUGE_MAX = { flesch_reading_ease: 100, forcast: 20, gunning_fog: 20, dale_chall: 12 };

// Half-circle readability gauge — value in the centre, arc coloured
// green (good) / red (needs work), metric label + target below.
const ReadabilityGauge = ({ metric }) => {
  const max = GAUGE_MAX[metric.key] || 100;
  const frac = Math.max(0, Math.min(1, (Number(metric.value) || 0) / max));
  const color = metric.good === null ? "#64748b" : metric.good ? "#16a34a" : "#ef4444";
  const arc = "M10 62 A50 50 0 0 1 110 62";
  return (
    <div style={{ textAlign: "center", padding: "4px 2px" }} title={metric.hint}>
      <svg viewBox="0 0 120 72" style={{ width: "100%", maxWidth: 140, display: "block", margin: "0 auto" }}>
        {/* background arc is a light tint of the metric's own color, not neutral grey */}
        <path d={arc} fill="none" stroke={`${color}33`} strokeWidth="9" strokeLinecap="round" pathLength="100" />
        <path d={arc} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" pathLength="100" strokeDasharray={`${frac * 100} 100`} />
        <text x="60" y="56" textAnchor="middle" fontSize="23" fontWeight="700" fill={color}>{metric.value}</text>
      </svg>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", lineHeight: 1.2, marginTop: 2 }}>{metric.label}</div>
      {metric.ideal && <div style={{ fontSize: 10, color: "#94a3b8" }}>Target: {metric.ideal}</div>}
    </div>
  );
};

// Only the 4 original formulas belong on the compact Readability card's gauge
// grid — originality.ai now returns 11, but the rest live in the "Readability
// Metrics" deep-dive section further down instead of cluttering this card.
const PRIMARY_READABILITY_KEYS = ["flesch_reading_ease", "forcast", "gunning_fog", "dale_chall"];

// Single half-circle percentage gauge (0-100, shown as a whole number — e.g.
// "49", never a decimal like "4.9") for AI Detection / Grammar & Spelling.
const PercentGauge = ({ percent, colorGood = true }) => {
  if (percent == null) return null;
  const pct = Math.round(Math.max(0, Math.min(100, percent)));
  const good = colorGood ? pct >= 70 : pct <= 30;
  const warn = colorGood ? (pct >= 40 && pct < 70) : (pct > 30 && pct <= 60);
  const color = good ? "#16a34a" : warn ? "#d97706" : "#dc2626";
  const arc = "M10 62 A50 50 0 0 1 110 62";
  const frac = pct / 100;
  return (
    <div style={{ textAlign: "center", padding: "4px 2px" }}>
      <svg viewBox="0 0 120 72" style={{ width: "100%", maxWidth: 150, display: "block", margin: "0 auto" }}>
        <path d={arc} fill="none" stroke="#eef2f7" strokeWidth="10" strokeLinecap="round" pathLength="100" />
        <path d={arc} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" pathLength="100" strokeDasharray={`${frac * 100} 100`} />
        <text x="60" y="56" textAnchor="middle" fontSize="26" fontWeight="800" fill={color}>{pct}%</text>
      </svg>
    </div>
  );
};

// originality.ai-style expandable metric list — numbered rows with a coloured
// value badge; click a row to reveal the typical Google-results range and the
// formula description.
const ReadabilityMetricList = ({ metrics }) => {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ border: "1px solid #eef2f7", borderRadius: 12, overflow: "hidden" }}>
      {metrics.map((m, i) => {
        const isOpen = open === m.key;
        const badge = m.grade ? `Grade ${Math.round(m.value)}` : `${m.value}`;
        const bg = m.good === null ? "#f1f5f9" : m.good ? "#dcfce7" : "#fee2e2";
        const fg = m.good === null ? "#475569" : m.good ? "#15803d" : "#dc2626";
        return (
          <div key={m.key} style={{ borderTop: i ? "1px solid #f1f5f9" : "none", background: isOpen ? "#f8fafc" : "#fff" }}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : m.key)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
            >
              <span style={{ color: "#94a3b8", fontSize: 13, width: 14 }}>{i + 1}</span>
              <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{m.label}</span>
              <span style={{ background: bg, color: fg, fontWeight: 700, fontSize: 12, padding: "3px 10px", borderRadius: 6, whiteSpace: "nowrap" }}>{badge}</span>
              <DownOutlined style={{ fontSize: 11, color: "#94a3b8", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
            </button>
            {isOpen && (
              <div style={{ padding: "0 14px 14px 40px" }}>
                {m.range && (
                  <div style={{ background: "#f1f5f9", borderRadius: 8, padding: "10px 12px", textAlign: "center", fontSize: 13, color: "#475569", marginBottom: 10 }}>
                    ~70% of the top results in Google had a {m.label} between ({m.range})
                  </div>
                )}
                <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, margin: 0 }}>{m.description}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────

const WritingIndex = ({ prefillTopic, onPrefillHandled, onSendForPost }) => {
  const { id: userId } = useParams();
  const location = useLocation();
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [postTitleForm] = Form.useForm();

  const [content, setContent] = useState("");
  const [formData, setFormData] = useState({});
  const [chatData, setChatData] = useState([]);
  // Snapshots of {content, chatData} taken right before an AI action mutates
  // the draft, so a single Undo can step back one action at a time.
  const [editHistory, setEditHistory] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [postTitleModalOpen, setPostTitleModalOpen] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [refresh, setRefresh] = useState(1);
  const [analysis, setAnalysis] = useState(null);
  const [composerText, setComposerText] = useState("");
  const [composerSettingsOpen, setComposerSettingsOpen] = useState(false);
  const [hashtags, setHashtags] = useState([]);
  const [hashtagLoading, setHashtagLoading] = useState(false);
  const [hashtagPanelOpen, setHashtagPanelOpen] = useState(false);
  const [editingClaim, setEditingClaim] = useState({ index: null, text: "" });
  const [fixingSentenceIndex, setFixingSentenceIndex] = useState(null);
  // Which module's sentence highlights are shown in the editor — mirrors
  // originality.ai's per-check highlighting. null = off.
  const [highlightMode, setHighlightMode] = useState("readability");
  const [retryingIndex, setRetryingIndex] = useState(null);
  const [fixingClaimIndex, setFixingClaimIndex] = useState(null);
  const [savedSelection, setSavedSelection] = useState("");
  // Guideline Checker — our own LLM-based feature (originality.ai's version
  // has no API access at all, confirmed against their published docs).
  const [guidelineCheck, setGuidelineCheck] = useState(null);
  const [guidelineCheckLoading, setGuidelineCheckLoading] = useState(false);
  const [customToneOpen, setCustomToneOpen] = useState(false);
  const [customToneValue, setCustomToneValue] = useState("");
  const [pendingSelection, setPendingSelection] = useState("");

  // Paste-your-own-content modal — lets you run the analyzer (Content
  // Intelligence panel) against text you wrote yourself, not just content
  // the model generated. Loading it in via addChat/setContent is the same
  // path location.state.initialContent already uses, so every existing
  // action (Analyze, Rewrite, Tone, etc.) works on it unchanged.
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const [isFullTextHumanize, setIsFullTextHumanize] = useState(false);

  const { chatHistories, saveChatHistory, updateChatHistory, deleteChatHistory, clearAllChatHistories } = useChatHistory();

  const plainText = useMemo(() => stripHtml(content), [content]);

  // Autosave: closing the tab mid-compose used to lose everything, because the
  // only way to persist was the manual "Save as draft" button.
  const { scheduleSave, getDraftId, setDraftId } = useDraftAutosave({
    enabled: !!plainText.trim() && !!userId,
    getPayload: () => ({
      ...formData,
      title: formData.topic || formData.title,
      content,
      status: "draft",
      userId,
      chatData,
    }),
    createUrl: "/admin/access-module/writing-assistance/add",
    updateUrl: (id) => `/admin/access-module/writing-assistance/draft/${id}`,
  });

  // Every AI action and manual edit lands in `content`/`chatData`, so watching
  // them here covers all the setContent call sites in one place.
  useEffect(() => {
    scheduleSave();
  }, [content, chatData, scheduleSave]);
  const wordCount = useMemo(() => (plainText.trim() ? plainText.trim().split(/\s+/).length : 0), [plainText]);
  const readingTime = Math.max(0, Math.ceil(wordCount / 200));
  const charCount = plainText.length;

  // Index of the newest "response" turn — that turn alone renders the live,
  // editable DescriptionEditor bound to `content`; every earlier turn is a
  // frozen HTML snapshot. Keeps exactly one .wa-editor-surface in the DOM.
  const lastResponseIndex = useMemo(() => {
    for (let i = chatData.length - 1; i >= 0; i--) if (chatData[i].type === "response") return i;
    return -1;
  }, [chatData]);

  const selectedLength = Form.useWatch("wordCount", form);
  const selectedLanguage = Form.useWatch("language", form);
  const isGenerating = loading || rewriteLoading || searchLoading;

  // ── Chat history auto-save ─────────────────────────────────────────────

  useEffect(() => {
    if (!chatData.some((item) => item.chatText?.trim())) return;
    if (currentChatId) { updateChatHistory(currentChatId, chatData, formData); return; }
    if (chatData.some((item) => item.type === "prompt")) {
      const saved = saveChatHistory(chatData, formData);
      if (saved) setCurrentChatId(saved.id);
    }
  }, [chatData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save text selection before it gets cleared by dropdown/button clicks.
  // Only selections made INSIDE the editor count — a stray selection in the
  // sidebar or analysis panel must never become an edit target.
  useEffect(() => {
    const onSelectionChange = () => {
      const editorEl = document.querySelector(".wa-editor-surface");
      if (!selectionInsideElement(editorEl)) return;
      const sel = window.getSelection?.()?.toString().trim();
      if (sel) setSavedSelection(sel);
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  // Any content change (typing, generation, applied action) invalidates a
  // previously saved selection — it no longer maps onto the document.
  useEffect(() => { setSavedSelection(""); }, [content]);

  // Prefill topic from parent (e.g. Research Assistant → Writing Assistant)
  useEffect(() => {
    if (prefillTopic) {
      form.setFieldsValue({ topic: prefillTopic });
      onPrefillHandled?.();
    }
  }, [prefillTopic, form, onPrefillHandled]);

  // Pre-fill content from "Write with Assistant" button in calendar (standalone route)
  useEffect(() => {
    const ic = location.state?.initialContent;
    if (!ic) return;
    setContent(ic);
    setChatData([{ type: "response", chatText: ic, date: new Date().toISOString() }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userId) return;
    request({
      url: `/admin/access-module/writing-assistance/${userId}?page=1&limit=8`,
      method: "GET",
      onSuccess: (data) => {
        const items = data?.data?.writingAssitance || [];
        setSavedItems(items);
        setSelectedItem(items[0] || null);
      },
      onError: () => setSavedItems([]),
    });
  }, [refresh, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI calls ───────────────────────────────────────────────────────────

  // "us" | "uk" when writing English, null for other languages
  const getEnglishVariant = () => {
    const language = form.getFieldValue("language") || "en";
    if (language !== "en") return null;
    return form.getFieldValue("englishVariant") || "us";
  };

  // Language code sent to the analyzer so suggestions/grammar follow the variant
  const analysisLanguage = () => {
    const language = form.getFieldValue("language") || "en";
    if (language !== "en") return language;
    return getEnglishVariant() === "uk" ? "en-GB" : "en-US";
  };

  // ── Stop control ───────────────────────────────────────────────────────
  // Every long-running flow registers a run id + AbortController. Stop aborts
  // the in-flight HTTP call and bumps the run id, so any handler still
  // awaiting discards its result instead of writing stale content/analysis.
  const runIdRef = useRef(0);
  const abortRef = useRef(null);

  // Close the floating language/length/keywords panel on an outside click,
  // matching the usual popover UX without pulling in antd's Popover (whose
  // rc-trigger positioning didn't settle reliably inside this layout).
  const settingsAnchorRef = useRef(null);
  useEffect(() => {
    if (!composerSettingsOpen) return;
    const onDocClick = (e) => {
      if (settingsAnchorRef.current && !settingsAnchorRef.current.contains(e.target)) {
        setComposerSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [composerSettingsOpen]);

  const beginProcess = () => {
    runIdRef.current += 1;
    abortRef.current = new AbortController();
    return runIdRef.current;
  };
  const isStale = (runId) => runId !== runIdRef.current;

  const handleStopProcess = () => {
    runIdRef.current += 1; // invalidate all in-flight handlers
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    setLoading(false);
    setSearchLoading(false);
    setRewriteLoading(false);
    setAnalysisLoading(false);
    setFixingSentenceIndex(null);
    ShowToast("Process stopped.", Severty.INFO);
  };

  // Thrown when the user pressed Stop mid-flight — handlers must swallow it
  const stoppedError = () => { const e = new Error("stopped"); e.stopped = true; return e; };

  const callWritingAI = async (prompt, raw = false, fast = false) => {
    const runId = runIdRef.current;
    const variant = getEnglishVariant();
    const finalPrompt = variant ? `${englishVariantNote(variant)}\n\n${prompt}` : prompt;
    const data = { prompt: finalPrompt, raw };
    // fast=true: use only gpt-4o-mini (openai) — the default deepseek model is
    // deepseek-reasoner, whose long chain-of-thought makes iterative rewrites
    // (Improve Readability, Fix Claim) take 30-90s each. gpt-4o-mini answers a
    // simple rewrite in a few seconds, so low-stakes loops feel instant.
    if (fast) data.models = ["openai"];
    const res = await request({ url: "/admin/access-module/research/deepseek", method: "POST", data, signal: abortRef.current?.signal });
    if (isStale(runId)) throw stoppedError();
    const generated = res?.data?.content;
    if (!generated) throw new Error("No content generated");
    return generated;
  };

  const analyzeReadabilityLite = async (text) => {
    try {
      const res = await request({
        url: "/admin/access-module/research/analyze",
        method: "POST",
        data: { text, language: analysisLanguage(), lite: true },
      });
      if (res?.data) {
        setAnalysis((prev) => (prev
          ? { ...prev, readability: res.data.readability, readability_details: res.data.readability_details, linkedin: res.data.linkedin }
          : res.data));
      }
      return res?.data;
    } catch { return null; }
  };

  const addChat = (type, chatText) =>
    setChatData((prev) => [...prev, { type, chatText, date: new Date().toISOString() }]);

  // Loads the user's own pasted text as the live draft — same shape as the
  // location.state.initialContent path, so it becomes editable and every
  // existing action (Analyze Content, Rewrite, Tone, Hashtags...) works on
  // it exactly as it would on model-generated content.
  const handleLoadOwnContent = () => {
    const text = pasteText.trim();
    if (!text) { ShowToast("Please paste some content first.", Severty.ERROR); return; }
    const html = text.replace(/\n/g, "<br/>");
    setContent(html);
    addChat("response", html);
    setPasteModalOpen(false);
    setPasteText("");
    ShowToast("Content loaded — click Analyze Content to run the analyzer on it.", Severty.SUCCESS);
  };

  // Call at the top of every handler that's about to mutate the draft, before
  // any setContent/addChat — captures exactly what to restore to on Undo.
  const snapshotBeforeEdit = () => {
    setEditHistory((prev) => [...prev, { content, chatData }].slice(-20));
  };

  const handleUndo = () => {
    if (!editHistory.length) return;
    const last = editHistory[editHistory.length - 1];
    setContent(last.content);
    setChatData(last.chatData);
    setEditHistory((prev) => prev.slice(0, -1));
    setAnalysis(null);
    ShowToast("Undone.", Severty.SUCCESS);
  };

  // ── Handlers ───────────────────────────────────────────────────────────

  const buildRawRequest = ({ topic, keywords, maxWords }) => {
    let req = `Write a LinkedIn post about: "${topic}"`;
    if (keywords) req += `\nKeywords to include: ${keywords}`;
    if (maxWords) req += `\nTarget length: ${maxWords} words`;
    return req;
  };

  const handleGenerate = async (values) => {
    if (!values?.topic?.trim()) { ShowToast("Please enter a topic!", Severty.ERROR); return; }
    const runId = beginProcess();
    setLoading(true);
    snapshotBeforeEdit();
    const finalFormData = { ...values, maxWords: values.wordCount === "custom" ? (values.customWordCount || null) : (values.wordCount || null) };
    setFormData(finalFormData);
    addChat("prompt", values.topic);
    try {
      const generated = await callWritingAI(buildRawRequest(finalFormData));
      setContent(generated.replace(/```html/g, "").replace(/```/g, "").trim());
      addChat("response", generated);
      setAnalysis(null);
      ShowToast("Content generated successfully!", Severty.SUCCESS);
    } catch (e) { if (!e?.stopped && !isStale(runId)) ShowToast("Error generating content.", Severty.ERROR); }
    finally { if (!isStale(runId)) setLoading(false); }
  };

  // Follow-up message once a draft already exists — edits `content` in place
  // and posts the result as the next assistant turn.
  const handleChatEdit = async (text) => {
    const runId = beginProcess();
    setRewriteLoading(true);
    snapshotBeforeEdit();
    addChat("prompt", text);
    try {
      const result = await callWritingAI(buildEditPrompt(text, content), true);
      const cleaned = result.replace(/```html/g, "").replace(/```/g, "").trim();
      setContent(cleaned);
      addChat("response", cleaned);
      setAnalysis(null);
    } catch (e) { if (!e?.stopped && !isStale(runId)) ShowToast("Failed to apply edit.", Severty.ERROR); }
    finally { if (!isStale(runId)) setRewriteLoading(false); }
  };

  // Bottom composer submit — first message on an empty draft generates from
  // scratch (reusing the Configure form's settings), every message after
  // that is interpreted as an edit instruction on the current draft.
  const submitComposer = async () => {
    const text = composerText.trim();
    if (!text || isGenerating) return;
    setComposerText("");
    const isFirstMessage = !chatData.length;
    if (isFirstMessage) {
      await handleGenerate({ ...form.getFieldsValue(), topic: text });
    } else {
      await handleChatEdit(text);
    }
    if (isFirstMessage) setComposerSettingsOpen(false);
  };

  const handleSearch = async () => {
    const topic = form.getFieldValue("topic");
    if (!topic?.trim()) { ShowToast("Please enter a topic!", Severty.ERROR); return; }
    const runId = beginProcess();
    setSearchLoading(true);
    try {
      const result = await callWritingAI(`Research the topic "${topic}" and return concise source-style notes, key angles, and useful facts for a LinkedIn writer.`);
      setContent(`<p>${result.replace(/\n{2,}/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`);
      addChat("response", result);
      ShowToast("Content generated successfully!", Severty.SUCCESS);
    } catch (e) { if (!e?.stopped && !isStale(runId)) ShowToast("Error generating content.", Severty.ERROR); }
    finally { if (!isStale(runId)) setSearchLoading(false); }
  };

  const handleAnalyze = async () => {
    if (!plainText.trim()) { ShowToast("Content is empty.", Severty.ERROR); return; }
    const runId = beginProcess();
    setAnalysisLoading(true);
    handleCheckGuidelines(); // fire alongside analysis, not awaited — runs in parallel, own loading state
    try {
      const res = await request({
        url: "/admin/access-module/research/analyze",
        method: "POST",
        data: { text: plainText, language: analysisLanguage() },
        signal: abortRef.current?.signal,
      });
      if (isStale(runId)) return; // user pressed Stop — discard the result
      if (!res?.data) throw new Error("No analysis returned");
      setAnalysis(res.data);
      ShowToast("Content analyzed!", Severty.SUCCESS);
    } catch { if (!isStale(runId)) ShowToast("Analysis failed.", Severty.ERROR); }
    finally { if (!isStale(runId)) setAnalysisLoading(false); }
  };

  const analyzeText = async (text) => {
    const runId = beginProcess();
    setAnalysisLoading(true);
    try {
      const res = await request({
        url: "/admin/access-module/research/analyze",
        method: "POST",
        data: { text, language: analysisLanguage() },
        signal: abortRef.current?.signal,
      });
      if (isStale(runId)) return; // user pressed Stop — discard the result
      if (!res?.data) throw new Error("No analysis returned");
      setAnalysis(res.data);
    } catch { if (!isStale(runId)) ShowToast("Analysis failed.", Severty.ERROR); }
    finally { if (!isStale(runId)) setAnalysisLoading(false); }
  };

  const handleFixContent = async () => {
    if (!plainText.trim() || !analysis?.facts?.length) return;
    const flagged = analysis.facts.filter((f) => f.verdict !== "Supported");
    if (!flagged.length) return;
    const runId = beginProcess();
    setRewriteLoading(true);
    snapshotBeforeEdit();

    // originality.ai ships a ready-made corrected sentence for claims it
    // marked false — swap those straight into the content, no LLM call.
    const withRewrite = flagged.filter((f) => f.rewrite);
    const withoutRewrite = flagged.filter((f) => !f.rewrite);

    try {
      let updated = content;
      withRewrite.forEach((f) => {
        const replaced = replaceSelectionInHtml(updated, f.claim, f.rewrite);
        if (replaced !== null) updated = replaced;
        else if (updated.includes(f.claim)) updated = updated.replace(f.claim, f.rewrite);
      });

      if (withoutRewrite.length) {
        const issues = withoutRewrite
          .map((f) => `- "${f.claim}": ${f.verdict}${f.explanation ? `. ${f.explanation}` : ""}`)
          .join("\n");
        const result = await callWritingAI(
          `You are a LinkedIn content editor. Rewrite ONLY the flagged sentences below within the post — leave every other sentence exactly as-is, including tone and structure.\n\nFACTUAL ISSUES TO CORRECT:\n${issues}\n\nINSTRUCTIONS:\n- Fix or remove flagged claims. Replace with accurate or general statements.\n- Do NOT add unverified new statistics.\n- Do NOT change any sentence not listed above.\n- Return only the improved post as clean HTML.\n\nOriginal post:\n${stripHtml(updated)}`
        );
        updated = result.replace(/```html/g, "").replace(/```/g, "").trim();
      }

      setContent(updated);
      setRewriteLoading(false);
      ShowToast(
        withoutRewrite.length ? "Content fixed! Re-analyzing..." : "Fixed using originality.ai's corrections! Re-analyzing...",
        Severty.SUCCESS
      );
      await analyzeText(stripHtml(updated));
    } catch (e) {
      if (!e?.stopped && !isStale(runId)) ShowToast("Failed to fix content.", Severty.ERROR);
      if (!isStale(runId)) setRewriteLoading(false);
    }
  };

  const handleRetryVerify = async () => {
    const { index, text: newClaim } = editingClaim;
    if (!newClaim.trim()) return;
    const originalClaim = analysis?.facts?.[index]?.claim || "";
    snapshotBeforeEdit();
    setEditingClaim({ index: null, text: "" });
    setRetryingIndex(index);
    try {
      const res = await request({ url: "/admin/access-module/research/verify-claim", method: "POST", data: { claim: newClaim.trim() } });
      if (res?.data) {
        setAnalysis((prev) => ({ ...prev, facts: prev.facts.map((f, i) => i === index ? { ...res.data, claim: newClaim.trim() } : f) }));
        // HTML-aware replace first, so claims spanning <strong>/<a> tags match.
        if (originalClaim && originalClaim !== newClaim.trim()) {
          const updated = replaceSelectionInHtml(content, originalClaim, newClaim.trim());
          if (updated !== null) setContent(updated);
          else if (content.includes(originalClaim)) setContent(content.replace(originalClaim, newClaim.trim()));
        }
        ShowToast("Claim re-verified and content updated!", Severty.SUCCESS);
      }
    } catch { ShowToast("Retry failed.", Severty.ERROR); }
    finally { setRetryingIndex(null); }
  };

  const handleFixClaim = async (index) => {
    const fact = analysis?.facts?.[index];
    if (!fact) return;
    snapshotBeforeEdit();
    setFixingClaimIndex(index);
    try {
      // originality.ai already ships a corrected version of false claims — use it
      // directly instead of spending an LLM call re-deriving one from scratch.
      if (fact.rewrite) {
        const updated = replaceSelectionInHtml(content, fact.claim, fact.rewrite);
        if (updated !== null) {
          setContent(updated);
        } else if (content.includes(fact.claim)) {
          setContent(content.replace(fact.claim, fact.rewrite));
        } else {
          ShowToast("Could not locate this claim in your post — try Edit & Retry instead.", Severty.ERROR);
          return;
        }
        setAnalysis((prev) => ({
          ...prev,
          facts: prev.facts.map((f, i) => (i === index
            ? { ...f, claim: fact.rewrite, verdict: "Supported", confidence: 100, explanation: "Corrected using originality.ai's suggested rewrite.", rewrite: null }
            : f)),
        }));
        ShowToast("Claim fixed using originality.ai's suggested correction!", Severty.SUCCESS);
        return;
      }

      const targetParagraph = findParagraphForClaim(content, fact.claim);
      if (!targetParagraph) {
        ShowToast("Could not locate this claim in your post — try Edit & Retry instead.", Severty.ERROR);
        return;
      }

      const prompt = `You are a professional fact-checking editor. The PARAGRAPH below contains a sentence that was flagged during fact-checking.

FLAGGED SENTENCE: "${fact.claim}"
VERDICT: ${fact.verdict}
REASON: ${fact.explanation || "Could not be verified with current data."}

TASK: Rewrite ONLY the flagged sentence inside this paragraph. Keep every other sentence in the paragraph exactly as-is, including any HTML tags like <strong>.
- If you know the correct, verifiable fact, use it.
- If uncertain, generalise the statement so it no longer makes an unverifiable specific claim (e.g. "is declining" instead of "fell by 59%").
- Do NOT invent a new specific statistic to replace it.
- Do NOT change anything else in the paragraph.

Return ONLY valid JSON — no markdown fences, no extra text:
{"paragraph": "<p>the full paragraph with the flagged sentence corrected, same HTML structure as input</p>", "corrected_sentence": "the corrected sentence as plain text only"}

PARAGRAPH:
${targetParagraph}`;

      const result = await callWritingAI(prompt, true, true);
      const parsed = extractJson(result);
      if (!parsed?.paragraph || !parsed?.corrected_sentence) throw new Error("Could not parse fix response");

      setContent(content.replace(targetParagraph, parsed.paragraph));

      const res = await request({ url: "/admin/access-module/research/verify-claim", method: "POST", data: { claim: parsed.corrected_sentence } });
      if (res?.data) {
        setAnalysis((prev) => ({ ...prev, facts: prev.facts.map((f, i) => (i === index ? { ...res.data, claim: parsed.corrected_sentence } : f)) }));
      }
      ShowToast("Claim fixed and re-verified!", Severty.SUCCESS);
    } catch {
      ShowToast("Failed to fix this claim.", Severty.ERROR);
    } finally {
      setFixingClaimIndex(null);
    }
  };

  const handleGenerateHashtags = async () => {
    const topic = form.getFieldValue("topic") || "";
    if (!plainText.trim() && !topic.trim()) { ShowToast("Add content or a topic first!", Severty.ERROR); return; }
    setHashtagLoading(true);
    setHashtagPanelOpen(false);
    try {
      const sample = plainText.slice(0, 600) || topic;
      const result = await callWritingAI(
        `Generate 12 relevant and currently trending LinkedIn hashtags for the content below.\n\nInclude a mix of:\n- Niche hashtags specific to the exact topic\n- Trending industry hashtags popular in ${new Date().getFullYear()}\n- Broad reach hashtags\n\nReturn ONLY a valid JSON array of hashtag strings (each starting with #), no explanation, no markdown:\n["#hashtag1", "#hashtag2", ...]\n\nContent:\n${sample}`
      );
      const m = result.match(/\[[\s\S]*?\]/);
      if (m) {
        const parsed = JSON.parse(m[0]);
        const tags = parsed.filter((t) => typeof t === "string" && t.startsWith("#"));
        setHashtags(tags);
        setHashtagPanelOpen(true);
      } else { ShowToast("Could not parse hashtags.", Severty.ERROR); }
    } catch { ShowToast("Failed to generate hashtags.", Severty.ERROR); }
    finally { setHashtagLoading(false); }
  };

  const handleExportCitations = async () => {
    const sources = (analysis?.facts || []).flatMap((f) => f.sources || []);
    if (!sources.length) {
      ShowToast("No sources to export.", Severty.ERROR);
      return;
    }
    const text = sources.map((s, i) => `${i + 1}. ${formatCitationAPA(s)}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      ShowToast("Citations copied to clipboard!", Severty.SUCCESS);
    } catch {
      ShowToast("Could not copy — clipboard access blocked.", Severty.ERROR);
    }
  };

  const handleCheckGuidelines = async () => {
    if (!plainText.trim()) {
      ShowToast("Write some content first.", Severty.ERROR);
      return;
    }
    setGuidelineCheckLoading(true);
    try {
      const res = await request({
        url: "/admin/access-module/research/guideline-check",
        method: "POST",
        data: { text: plainText, guideline: "AP-inspired" },
      });
      if (!res?.data) throw new Error("No guideline check result returned");
      setGuidelineCheck(res.data);
    } catch {
      ShowToast("Guideline check failed.", Severty.ERROR);
    } finally {
      setGuidelineCheckLoading(false);
    }
  };

  const handleCopyGuidelineSuggestion = async (suggestion) => {
    try {
      await navigator.clipboard.writeText(suggestion);
      ShowToast("Suggestion copied!", Severty.SUCCESS);
    } catch {
      ShowToast("Could not copy — clipboard access blocked.", Severty.ERROR);
    }
  };

  const addHashtagToContent = (tag) => {
    if (plainText.includes(tag)) return;
    snapshotBeforeEdit();
    setContent((prev) => prev.trim() + (prev.trim() ? " " + tag : tag));
  };

  const addAllHashtagsToContent = () => {
    const newTags = hashtags.filter((t) => !plainText.includes(t));
    if (!newTags.length) { setHashtagPanelOpen(false); return; }
    snapshotBeforeEdit();
    setContent((prev) => prev.trim() + "\n\n" + newTags.join(" "));
    setHashtagPanelOpen(false);
    ShowToast("Hashtags added!", Severty.SUCCESS);
  };

  const handleApplySuggestion = async (suggestion) => {
    if (!plainText.trim()) { ShowToast("Content is empty.", Severty.ERROR); return; }
    const runId = beginProcess();
    setRewriteLoading(true);
    snapshotBeforeEdit();
    try {
      const result = await callWritingAI(
        `Apply ONLY this specific improvement to the LinkedIn post below. Keep everything else unchanged. Return only the improved post as clean HTML.\n\nImprovement: "${suggestion}"\n\nPost:\n${plainText}`
      );
      setContent(result.replace(/```html/g, "").replace(/```/g, "").trim());
      setAnalysis(null);
      ShowToast("Suggestion applied!", Severty.SUCCESS);
    } catch (e) { if (!e?.stopped && !isStale(runId)) ShowToast("Failed to apply suggestion.", Severty.ERROR); }
    finally { if (!isStale(runId)) setRewriteLoading(false); }
  };

  // direction: "default" (first attempt) | "too_hard" (last attempt still
  // outside on the hard side — simplify more) | "too_easy" (last attempt
  // overshot past the easy edge, most often Dale-Chall — simplify LESS).
  // The old version applied the same maximally-aggressive rules (10-word hard
  // cap, replace every 3+ syllable word) on every attempt regardless of
  // feedback direction, which reliably overshot Dale-Chall past its 5.6 floor
  // every time — the rules themselves never actually changed based on what
  // went wrong last time.
  const buildReadabilityPrompt = (text, feedback = "", direction = "default") => {
    const inputWordCount = text.trim().split(/\s+/).filter(Boolean).length;

    const rulesByDirection = {
      default: `MODERATE SIMPLIFICATION — apply these, no exceptions:
1. Aim for an AVERAGE sentence length of about 12-14 words — not every sentence has to be short, just avoid long run-ons. Split any sentence over ~18 words into two.
2. Replace genuinely rare or technical words with common everyday alternatives (e.g. "utilise" → "use", "facilitate" → "help", "approximately" → "about") — but leave ordinary words alone even if they have 3+ syllables ("content", "creation", "individual", "important" are fine as-is).
3. Convert passive sentences to active voice where natural.
4. Use contractions where natural: "it is" → "it's", "do not" → "don't".
5. Write clearly for a professional LinkedIn audience — plain, direct, but not childish.
6. Keep ALL facts, names, statistics, examples, and the core message — every idea in the input must still be present in the output.`,
      too_hard: `HARD CAP — this content is still too difficult, apply these more aggressively than before:
1. HARD CAP: every sentence must be 10 words or fewer. Split anything longer into two or three separate sentences — keep every piece of information from the original, just spread across more sentences.
2. Replace EVERY word with 3+ syllables with a 1-2 syllable phrase of similar meaning: "automation" → "auto work", "individual" → "single person", "demonstrate" → "show".
3. Convert every passive sentence to active voice.
4. Each paragraph: 1-2 short sentences MAX.
5. Write like you're explaining it to a smart 12-year-old.
6. Keep ALL facts, names, statistics, examples, and the core message — every idea in the input must still be present in the output.`,
      too_easy: `PULL BACK — the last version was simplified TOO FAR (overshot the easy edge, most likely on Dale-Chall). Do NOT keep shortening. Instead:
1. Use LONGER, more natural sentences — aim for about 14-17 words average, not short choppy fragments. Combine some of the short sentences back together with a connector ("and", "because", "which").
2. Reintroduce a FEW normal professional words you over-simplified last time — words like "creation", "individual", "significant", "approach" are fine; you don't need to dumb down every word, only genuinely obscure ones.
3. Keep it in active voice and contractions are fine, but stop chasing "as simple as possible" — the goal is comfortably-inside-the-range, not the easiest possible text.
4. Keep ALL facts, names, statistics, examples, and the core message — every idea in the input must still be present in the output.`,
    };
    const rules = rulesByDirection[direction] || rulesByDirection.default;

    return `You are a professional editor. Rewrite the content below so its Overall Readability Score hits 80+/100. That score is the average of two formulas, each graded from the HARD edge of its range (0%) up to the EASY edge (100%):
1. Flesch Reading Ease — driven by average sentence length and average syllables per word. Target range: 31-62.4 (31=hardest, 62.4=easiest). Aim for about 55-60 — close to the easy end, but comfortably inside.
2. Dale-Chall Readability Grade — driven by average sentence length AND the percentage of words NOT on a list of ~3,000 common, familiar words. Target range: 5.6-8.4 (8.4=hardest, 5.6=easiest). Aim for about 6.0-6.5 — close to the easy end, but comfortably inside. This formula cares about genuinely common everyday words, not just short ones — a short but unusual word can still count as "difficult" here, and over-simplifying pushes it BELOW 5.6, which is just as much a fail as staying too hard.

Vague tweaks will NOT move either score — you must restructure. STAY INSIDE BOTH RANGES — going past the easy edge (Flesch above 62.4, or Dale-Chall below 5.6) is NOT graded softer, it scores a flat 0% for that formula, same as failing to simplify at all. There is no partial credit for overshooting.
${feedback}
WORD COUNT: the input below has ${inputWordCount} words. Keep your output reasonably close to that length — restoring detail via natural sentence splits is fine, but don't pad or cut content just to hit a number.

${rules}

Return ONLY clean HTML with <p> and <strong> tags. No preamble, no explanation.

Content to rewrite (${inputWordCount} words):
${text}`;
  };

  // Score with the SAME engine that fills the Readability card's overall
  // score (0-100 — average of Flesch Reading Ease and Dale-Chall Grade, each
  // normalised against its own target band, with a taper past the good edge
  // rather than a hard cliff). Also returns whether BOTH formulas actually
  // land inside their real target range (the gauge's own `good` flag) —
  // the taper means a high score alone doesn't guarantee that, so Improve
  // Readability checks range membership separately rather than trusting the
  // score in isolation. Returns { score, inRange } — score/inRange null on failure.
  const fetchServerReadabilityDetail = async (text) => {
    try {
      const res = await request({
        url: "/admin/access-module/research/analyze",
        method: "POST",
        data: { text, language: analysisLanguage(), lite: true },
        signal: abortRef.current?.signal,
      });
      const score = res?.data?.readability_details?.overall_score;
      const metrics = res?.data?.readability_details?.metrics || [];
      const flesch = metrics.find((m) => m.key === "flesch_reading_ease");
      const daleChall = metrics.find((m) => m.key === "dale_chall");
      const inRange = flesch && daleChall ? Boolean(flesch.good && daleChall.good) : null;
      return {
        score: score != null ? Number(score) : null,
        inRange,
        flesch: flesch ? { value: flesch.value, good: flesch.good } : null,
        daleChall: daleChall ? { value: daleChall.value, good: daleChall.good } : null,
      };
    } catch { return { score: null, inRange: null, flesch: null, daleChall: null }; }
  };

  // Which direction the NEXT rewrite attempt should push, based on the last
  // candidate's raw values (not just the score) — an overshoot (too easy) and
  // an undershoot (too hard) need OPPOSITE instructions, so this can't be a
  // single generic "try harder" retry. If either formula overshot past its
  // easy edge, that takes priority to pull back (that's the failure mode that
  // was silently compounding every retry before this fix).
  const readabilityDirection = (detail) => {
    if (!detail?.flesch || !detail?.daleChall) return "default";
    const fleschTooEasy = detail.flesch.good === false && detail.flesch.value > 62.4;
    const daleChallTooEasy = detail.daleChall.good === false && detail.daleChall.value < 5.6;
    if (fleschTooEasy || daleChallTooEasy) return "too_easy";
    const fleschTooHard = detail.flesch.good === false && detail.flesch.value < 31;
    const daleChallTooHard = detail.daleChall.good === false && detail.daleChall.value > 8.4;
    if (fleschTooHard || daleChallTooHard) return "too_hard";
    return "default";
  };

  const handleImproveReadability = async () => {
    if (!plainText.trim()) { ShowToast("Content is empty.", Severty.ERROR); return; }
    const runId = beginProcess();
    setRewriteLoading(true);
    snapshotBeforeEdit();
    try {
      const TARGET = 80;  // stop iterating once the overall score hits 80+/100 AND both formulas are in-range
      const ACCEPT = 75;  // user-facing bar: above 75 (while in-range) counts as success
      const MAX_ATTEMPTS = 3;

      // Fall back to a Flesch-only local approximation only if the server is
      // unreachable (Dale-Chall's difficult-word list isn't computable client-side).
      // inRange is null in that fallback case — we can't verify Dale-Chall locally.
      const detailOf = async (text) => {
        const server = await fetchServerReadabilityDetail(text);
        if (server.score != null) return server;
        const local = Math.max(0, Math.min(100, ((computeFlesch(text) - 31) / 31.4) * 100));
        return { score: local, inRange: null, flesch: null, daleChall: null };
      };
      // A candidate is "better" if it's in-range and the current best isn't, or
      // if both (or neither) are in-range but it scores higher. This stops the
      // taper-forgiving score from letting an out-of-range overshoot win just
      // because it happens to score higher than an in-range attempt.
      const isBetter = (candidate, best) => {
        if (candidate.inRange && !best.inRange) return true;
        if (!candidate.inRange && best.inRange) return false;
        return candidate.score > best.score;
      };

      let currentPlain = plainText;
      let bestHtml = content;
      // Reuse the score already on the Readability card (this button only shows
      // after analysis) instead of spending another round trip to re-measure.
      // We don't know its in-range status without re-fetching metrics, so treat
      // it as unknown (null) — the first generated attempt will still be judged
      // fairly by isBetter() since inRange:true beats inRange:null via the same rule.
      let best = analysis?.readability_details?.overall_score != null
        ? { score: Number(analysis.readability_details.overall_score), inRange: null, flesch: null, daleChall: null }
        : await detailOf(plainText);
      if (isStale(runId)) return; // user pressed Stop — keep the document as-is

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        if (best.inRange && best.score >= TARGET) break;

        const direction = attempt === 1 ? "default" : readabilityDirection(best);
        const feedback = attempt === 1
          ? ""
          : direction === "too_easy"
            ? `\nFEEDBACK FROM YOUR LAST ATTEMPT: That version scored ${best.score.toFixed(0)}/100 but OVERSHOT past the easy edge (Flesch ${best.flesch?.value ?? "?"}, Dale-Chall ${best.daleChall?.value ?? "?"}) — too simple. Do NOT simplify further. Pull back: use longer, more natural sentences and stop replacing every word.\n`
            : `\nFEEDBACK FROM YOUR LAST ATTEMPT: That version scored ${best.score.toFixed(0)}/100 but is still too close to or outside the hard edge (Flesch ${best.flesch?.value ?? "?"}, Dale-Chall ${best.daleChall?.value ?? "?"}) — target is 80+/100 with BOTH formulas strictly inside their range. Simplify further.\n`;

        const prompt = buildReadabilityPrompt(currentPlain, feedback, direction);
        const result = await callWritingAI(prompt, true, true); // fast=true: single model, no judge comparison
        const cleaned = result.replace(/```html/g, "").replace(/```/g, "").trim();
        const newPlain = stripHtml(cleaned);
        const candidate = await detailOf(newPlain);
        if (isStale(runId)) return; // stopped mid-loop — discard this attempt

        currentPlain = newPlain;
        if (isBetter(candidate, best)) {
          best = candidate;
          bestHtml = cleaned;
        }
      }

      setContent(bestHtml);
      addChat("response", bestHtml);
      // Refresh the score card, then reuse that exact number (and in-range
      // status) in the toast so the card and the toast always agree.
      const cardResult = await analyzeReadabilityLite(stripHtml(bestHtml));
      const cardMetrics = cardResult?.readability_details?.metrics || [];
      const cardFlesch = cardMetrics.find((m) => m.key === "flesch_reading_ease");
      const cardDaleChall = cardMetrics.find((m) => m.key === "dale_chall");
      const finalInRange = cardFlesch && cardDaleChall ? Boolean(cardFlesch.good && cardDaleChall.good) : best.inRange;
      const finalScore = cardResult?.readability_details?.overall_score != null
        ? Number(cardResult.readability_details.overall_score)
        : best.score;

      if (finalInRange && finalScore >= ACCEPT) {
        ShowToast(`Readability improved to ${finalScore.toFixed(0)}/100 — both formulas in range!`, Severty.SUCCESS);
      } else if (finalInRange === false) {
        ShowToast(`Improved to ${finalScore.toFixed(0)}/100, but one formula is still outside its target range — simplified as much as possible while keeping facts intact.`, Severty.SUCCESS);
      } else {
        ShowToast(`Improved to ${finalScore.toFixed(0)}/100 — simplified as much as possible while keeping facts intact.`, Severty.SUCCESS);
      }
    } catch (e) { if (!e?.stopped && !isStale(runId)) ShowToast("Failed to improve readability.", Severty.ERROR); }
    finally { if (!isStale(runId)) setRewriteLoading(false); }
  };

  const buildTonePrompt = (tone, text) => {
    const toneGuides = {
      professional: `TASK: Rewrite the content below in a FORMAL PROFESSIONAL tone.

THIS IS A COMPLETE REWRITE — not a light edit. The output must sound NOTICEABLY different from the input.

MANDATORY RULES — every single one must be visible in the output:
1. ZERO contractions. "it's" → "it is". "don't" → "do not". "we're" → "we are". Search every word.
2. Third-person framing: replace "I" with "one" or restructure. Replace "you" with "professionals" or "leaders".
3. Formal vocabulary ONLY: "use" → "utilise", "show" → "demonstrate", "help" → "facilitate", "big" → "significant", "get" → "obtain".
4. Long, structured sentences with subordinate clauses. Minimum 15 words per sentence.
5. Every paragraph starts with a formal topic sentence. No fragments. No single-line paragraphs.
6. Remove ALL casual phrases: "look,", "here's the thing", "honestly", "you know", "pretty", "super", "amazing".
7. Closing: a formal rhetorical question using words like "How might...", "In what ways...", "To what extent...".

EXAMPLE of the required transformation:
INPUT:  "Look, AI is changing things fast. We can't ignore it."
OUTPUT: "Artificial intelligence is fundamentally transforming the operational landscape. Organisations that fail to acknowledge this shift risk significant competitive disadvantage."

Now apply this to the ENTIRE content below. Make the change OBVIOUS and DRAMATIC.`,

      friendly: `TASK: Rewrite the content below in a FRIENDLY, WARM tone.

THIS IS A COMPLETE REWRITE — not a light edit. The output must sound NOTICEABLY different from the input.

MANDATORY RULES — every single one must be visible in the output:
1. Write like you're talking to a colleague you genuinely like. Warm, encouraging, approachable.
2. Use contractions EVERYWHERE: "it is" → "it's", "do not" → "don't", "I am" → "I'm".
3. Address the reader directly as "you" and include yourself with "we" — this is a two-way conversation.
4. Add at least ONE warm, human touch: an encouragement ("and honestly, you're probably closer than you think"), an empathetic acknowledgment, or gentle humour.
5. Soften absolutes: "must" → "it really helps to", "never" → "try to avoid".
6. Replace formal words with everyday ones: "utilise" → "use", "facilitate" → "help", "significant" → "big".
7. Close with an inviting, low-pressure question: "What's worked for you?" / "Would love to hear your take."

EXAMPLE of the required transformation:
INPUT:  "Professionals must prioritise continuous learning to remain competitive."
OUTPUT: "Here's something I keep coming back to: the people who keep learning are the ones who keep growing. And the good news? You don't need a course or a certificate to start — just curiosity."

Now apply this to the ENTIRE content below. Make the change OBVIOUS and DRAMATIC.`,

      persuasive: `TASK: Rewrite the content below in a PERSUASIVE tone built to convince and move the reader to act.

THIS IS A COMPLETE REWRITE — not a light edit. The output must sound NOTICEABLY different from the input.

MANDATORY RULES — every single one must be visible in the output:
1. Open by naming the cost of inaction or the size of the opportunity — create urgency in line one.
2. Frame EVERY point as a benefit to the reader: not what something is, but what it gets them.
3. Use "you" relentlessly — the reader is the hero of every sentence.
4. Handle the obvious objection head-on: "You might think X. Here's why that's backwards."
5. Use the rule of three at least once: "faster decisions, sharper focus, better outcomes."
6. Short, confident sentences. No hedging words: cut "maybe", "perhaps", "might", "somewhat", "I think".
7. End with a strong, specific call-to-action or decision question — tell the reader exactly what to do or decide next.

EXAMPLE of the required transformation:
INPUT:  "AI tools can help improve productivity in many workplaces."
OUTPUT: "Every week you delay adopting AI tools, your competitors bank hours you're still spending on busywork. The teams that switched are shipping faster, deciding sharper, and hiring slower. What's stopping you from running the same play?"

Now apply this to the ENTIRE content below. Make the change OBVIOUS and DRAMATIC.`,

      casual: `TASK: Rewrite the content below in a CASUAL, RELAXED tone.

THIS IS A COMPLETE REWRITE — not a light edit. The output must sound NOTICEABLY different from the input.

MANDATORY RULES — every single one must be visible in the output:
1. Start with a short punchy opener of 5-8 words. Fragment is fine.
2. Use contractions EVERYWHERE: "it is" → "it's", "do not" → "don't", "I am" → "I'm".
3. First person throughout: "I", "we", "you". Talk directly TO the reader like a friend over coffee.
4. Mix very short sentences (4-7 words) with longer ones. Break rhythm intentionally.
5. Include at least ONE conversational aside: "And honestly?" / "Here's the thing —" / "I'll be real with you."
6. Replace ALL formal words: "utilise" → "use", "facilitate" → "help", "demonstrate" → "show", "significant" → "big", "endeavour" → "try".
7. Casual closing question that sounds like you actually want an answer: "What do you think?" / "Has this happened to you?" / "Am I the only one who feels this way?"

EXAMPLE of the required transformation:
INPUT:  "Professionals must prioritise digital transformation to remain competitive."
OUTPUT: "Here's the thing — if you're not thinking about digital right now, you're already falling behind. And that's not a scare tactic. It's just true."

Now apply this to the ENTIRE content below. Make the change OBVIOUS and DRAMATIC.`,

      executive: `TASK: Rewrite the content below in an EXECUTIVE tone — strategic, concise, leadership-oriented.

THIS IS A COMPLETE REWRITE — not a light edit. The output must sound NOTICEABLY different from the input.

MANDATORY RULES — every single one must be visible in the output:
1. Lead with the conclusion — the single strategic takeaway goes in sentence one. No warm-up.
2. Every paragraph opens with an outcome or action verb: "Drive", "Build", "Accelerate", "Deliver", "Capture", "Reduce", "Achieve".
3. Cut ALL emotional language, personal stories, feelings, and reflections. Zero tolerance.
4. Make every sentence point to a business result, decision, or competitive implication.
5. Use executive language sparingly but precisely: "ROI", "bottom line", "scalable", "market share", "operational efficiency", "strategic priority".
6. Tight paragraphs: 1-2 sentences MAX. If any number or stat exists in the original, make it the FIRST thing in a sentence.
7. Closing: a sharp strategic question or decision frame — "The question isn't whether to act — it's how fast." / "What's the cost of waiting another quarter?"

EXAMPLE of the required transformation:
INPUT:  "I think leadership is about showing up for your team."
OUTPUT: "Effective leadership directly drives retention, output quality, and team-level ROI. Organisations that invest in leadership development outperform peers on every key performance metric."

Now apply this to the ENTIRE content below. Make the change OBVIOUS and DRAMATIC.`,

      inspirational: `TASK: Rewrite the content below in an INSPIRATIONAL tone — motivational, positive, story-driven.

THIS IS A COMPLETE REWRITE — not a light edit. The output must sound NOTICEABLY different from the input.

MANDATORY RULES — every single one must be visible in the output:
1. Open with a quiet, powerful observation or a moment of tension — never a statistic, never "In today's world".
2. Build a narrative arc: struggle or doubt → turning point → insight → possibility.
3. Speak to the reader's potential: "you're capable of more than this moment suggests."
4. Use vivid, concrete imagery over abstractions: not "overcome challenges" but "the Monday you almost quit".
5. Short, rhythmic sentences at emotional peaks. One-line paragraphs are encouraged for the key lines.
6. Stay grounded — no invented statistics, no fake quotes, no toxic positivity. Real hope, not hype.
7. Close with an uplifting question or gentle challenge that invites reflection: "What would you attempt if you trusted yourself a little more?"

EXAMPLE of the required transformation:
INPUT:  "Persistence is important for career growth."
OUTPUT: "Nobody claps for the tenth rejection. But the tenth rejection is where most people stop — and where the ones we later call 'lucky' kept going. Growth rarely announces itself. It compounds quietly, one unglamorous Tuesday at a time."

Now apply this to the ENTIRE content below. Make the change OBVIOUS and DRAMATIC.`,
    };

    const guide = toneGuides[tone.toLowerCase()] || `Rewrite the content in a ${tone} tone. Make the change obvious and dramatic — this should be a complete rewrite, not minor edits.`;

    return `${guide}

Keep ALL facts, statistics, and the core message intact — only the voice and structure change.
Return ONLY clean HTML using <p> and <strong> tags. No preamble, no explanation, no "Here is the rewritten version:".

ORIGINAL CONTENT TO REWRITE:
${text}`;
  };

  // Live selection counts only when it was made inside the editor surface
  const getEditorSelection = () => {
    const editorEl = document.querySelector(".wa-editor-surface");
    if (!selectionInsideElement(editorEl)) return "";
    return window.getSelection?.()?.toString().trim() || "";
  };

  const handleAction = async (type, toneStr = "", selectionOverride = "") => {
    if (!plainText.trim()) { ShowToast("Content is empty.", Severty.ERROR); return; }
    const runId = beginProcess();
    const selectionText = selectionOverride || getEditorSelection() || savedSelection;
    const isPartial = !!selectionText;
    const targetText = isPartial ? selectionText : plainText;
    setSavedSelection("");
    snapshotBeforeEdit();
    if (!isPartial) addChat("prompt", actionLabel(type, toneStr));

    const promptMap = {
      rewrite: `TASK: Completely rewrite the content below. The output must be UNRECOGNISABLY different from the input.

MANDATORY RULES:
1. The opening sentence must be ENTIRELY different — different words, different structure, different angle.
2. Do NOT reuse any phrase longer than 3 words from the original. If you catch yourself copying a phrase, stop and rephrase.
3. Change the sentence structure of every sentence — if the original is long, make it short. If it starts with the subject, start with something else.
4. Approach the same idea from a fresh angle — different metaphor, different example, different entry point.
5. Keep the same core message and all facts/names intact — only the expression changes.
6. Same approximate length as the original.

EXAMPLE:
INPUT:  "The FIFA World Cup 2026 goes far beyond a mere tournament — it's a powerful testament to unity."
OUTPUT: "Sport has a rare power. The 2026 World Cup doesn't just crown a champion — it reminds 48 nations that what unites us is stronger than what divides us."

Return ONLY clean HTML with <p> and <strong> tags. No preamble. No "Here is the rewritten version:".

ORIGINAL:
${targetText}`,
      expand: isPartial
        ? `TASK: Expand the SELECTED TEXT below slightly — enrich it in place. This is a small, local expansion, NOT a rewrite of a post.

MANDATORY RULES:
1. Grow the selection to roughly 1.5-2× its current length — no more.
2. Keep the SAME shape as the input: one sentence becomes 1-3 sentences, one paragraph stays ONE paragraph. Do NOT add new paragraphs, line breaks, headings, or lists.
3. Enrich the EXISTING point with one concrete detail, example, or implication — do not introduce new topics, hooks, or closing questions.
4. Keep the original wording where possible; weave the additions around it.
5. Keep all facts, names, and the core message exactly intact.
6. The result must read naturally in place of the original selection, flowing with the text before and after it.

EXAMPLE:
INPUT:  "The 2026 World Cup features 48 teams for the first time."
OUTPUT: "The 2026 World Cup features 48 teams for the first time — a 50% jump from the 32-team format used since 1998, giving 16 more nations their moment on the world stage."

Return ONLY the expanded text, matching the input's HTML shape (bare text if the input has no <p> tags). No preamble.

SELECTED TEXT:
${targetText}`
        : `TASK: Expand the content below. The output must be NOTICEABLY longer and richer than the input.

MANDATORY RULES — every one must be visible in the output:
1. Add at least 2 completely new paragraphs that did not exist in the original.
2. For every claim or point in the original, add ONE of: a real-world example, a statistic, an analogy, or a professional implication.
3. Add a "why it matters" paragraph that explains the broader significance to the reader's career or business.
4. If the original has a question at the end, add 2-3 sentences before it that build toward it more powerfully.
5. Do NOT repeat the same sentence twice in different words — every sentence must add new information.
6. Keep all original facts, names, and the core message exactly intact.
7. Final output should be at least 1.5× the length of the original.

EXAMPLE:
INPUT:  "The 2026 World Cup features 48 teams for the first time."
OUTPUT: "The 2026 World Cup features 48 teams for the first time — a 50% increase from the 32-team format used since 1998. This expansion means 16 additional nations get their moment on the world stage, including first-time qualifiers from regions historically underrepresented in the tournament. For businesses, it signals a broader audience, more broadcast markets, and a sponsorship opportunity that spans three continents simultaneously."

Return ONLY clean HTML with <p> and <strong> tags. No preamble.

ORIGINAL:
${targetText}`,
      condense: `TASK: Condense the content below to its sharpest, most impactful form. The output must be NOTICEABLY shorter than the input.

MANDATORY RULES — every one must be visible in the output:
1. The final output must be no more than HALF the word count of the original.
2. Keep ONLY the single strongest point from each paragraph — cut everything else.
3. Remove ALL of these on sight: adjectives that don't add meaning ("powerful", "transformative", "incredible"), adverbs ("very", "quite", "truly", "deeply"), filler openers ("In today's world", "It is important to note", "As we all know").
4. Merge any two sentences that say the same thing into one sharper sentence.
5. The hook (first sentence) must be the single strongest line — rewrite it if needed to pack maximum impact.
6. Keep the closing question or CTA exactly as-is.
7. Every word that remains must earn its place.

EXAMPLE:
INPUT:  "The FIFA World Cup 2026 goes far beyond a mere tournament — it's a truly powerful testament to the transformative force of sport and the incredible unity it creates among nations around the world."
OUTPUT: "The 2026 World Cup isn't just a tournament. It's proof that sport still unites nations like nothing else can."

Return ONLY clean HTML with <p> and <strong> tags. No preamble.

ORIGINAL:
${targetText}`,
      improve: `TASK: Improve the writing quality of the content below WITHOUT changing its meaning, facts, or intent.

MANDATORY RULES:
1. Fix every grammar, punctuation, and word-choice error.
2. Improve clarity: replace vague phrases with precise ones, split confusing sentences.
3. Improve flow: vary sentence length, add smooth transitions, remove awkward constructions.
4. Cut redundancy — if two sentences say the same thing, merge them into the stronger one.
5. Keep ALL facts, names, numbers, and the original message exactly intact.
6. Keep roughly the same length and structure — this is polishing, not rewriting.
7. Keep the existing tone; just make it read better.

Return ONLY clean HTML with <p> and <strong> tags. No preamble.

ORIGINAL:
${targetText}`,
      simplify: `TASK: Simplify the language of the content below while keeping the exact same meaning.

MANDATORY RULES:
1. Replace every complex or multi-syllable word with a simpler everyday one: "utilise" → "use", "demonstrate" → "show", "approximately" → "about", "facilitate" → "help".
2. Split any sentence longer than 15 words into shorter ones.
3. Remove jargon — or, if a technical term is essential, keep it and explain it in plain words.
4. Convert passive voice to active voice.
5. Keep ALL facts, names, numbers, examples, and the core message — simplify the words, never the substance.
6. Do not shorten or summarise — every idea in the input must still be present.

Return ONLY clean HTML with <p> and <strong> tags. No preamble.

ORIGINAL:
${targetText}`,
      grammar: `TASK: Fix grammar, spelling, and punctuation in the content below. This is a CORRECTION pass, not a rewrite.

MANDATORY RULES:
1. Correct every grammar error: subject-verb agreement, tense consistency, article usage, prepositions.
2. Fix all spelling mistakes and typos.
3. Fix punctuation: commas, apostrophes, quotation marks, sentence-ending marks.
4. Do NOT change the wording, style, tone, structure, or meaning beyond what a correction requires.
5. If a sentence is already correct, return it EXACTLY as-is, character for character.
6. Keep all HTML tags exactly where they are.

Return ONLY clean HTML with the same structure as the input. No preamble.

ORIGINAL:
${targetText}`,
      professional: `TASK: Make the content below more PROFESSIONAL while preserving its meaning and message.

MANDATORY RULES:
1. Remove slang, casual asides, and overly familiar phrasing.
2. Replace casual words with polished ones: "stuff" → "material", "a lot of" → "many", "get" → "gain" — but stay natural, not stiff.
3. Tighten every sentence: cut filler ("really", "actually", "basically", "just").
4. Ensure a confident, credible voice — no hedging ("I guess", "sort of", "kind of").
5. Keep the original structure, facts, examples, and intent intact — this is elevating the register, not rewriting the ideas.
6. Keep it human — do not turn it into corporate jargon or buzzword soup.

Return ONLY clean HTML with <p> and <strong> tags. No preamble.

ORIGINAL:
${targetText}`,
      engaging: `TASK: Make the content below more ENGAGING — stronger hooks, better rhythm, more reasons to keep reading.

MANDATORY RULES:
1. Sharpen the opening line into a genuine scroll-stopper: tension, contrast, a surprising angle, or a direct question.
2. Break up any dense paragraph — 1-2 sentences per paragraph, blank line between each.
3. Add rhythm: mix short punchy sentences with longer ones. One-line paragraphs for key moments.
4. Speak to the reader: use "you" where it fits naturally.
5. Where appropriate, turn a flat statement into a mini-story, contrast, or concrete image — without inventing facts or statistics.
6. Keep ALL facts, names, numbers, and the core message exactly intact.
7. End on a line that invites a response or leaves the reader thinking.

Return ONLY clean HTML with <p> and <strong> tags. No preamble.

ORIGINAL:
${targetText}`,
      tone:     buildTonePrompt(toneStr, targetText),
      humanize: `You are a professional ghostwriter. Rewrite the text below so it sounds like a real person — not an AI or corporate copywriter.\n1. Replace buzzwords: "leverage"→"use", "utilize"→"use"\n2. Use contractions: "we are"→"we're", "it is"→"it's"\n3. Vary sentence length — mix short (5-8 words) with longer\n4. Cut passive voice\n5. Remove filler: "In today's world", "It is important to note"\n6. Add ONE short personal observation\n7. Keep all facts, stats, and names EXACTLY as-is\nReturn only clean HTML with <p> and <strong> tags. No preamble.\n\nOriginal:\n${targetText}`,
    };

    setRewriteLoading(true);
    try {
      const basePrompt = promptMap[type] || promptMap.rewrite;
      // For selections, prepend excerpt rules so the model edits ONLY the
      // selected text and returns it in a splice-friendly shape.
      const prompt = isPartial ? EXCERPT_RULES + basePrompt : basePrompt;
      const result = await callWritingAI(prompt, true);
      const cleaned = result.replace(/```html/g, "").replace(/```/g, "").trim();
      let finalHtml;
      if (isPartial) {
        // DOM-based replacement keeps surrounding formatting/links/lists intact
        const updated = replaceSelectionInHtml(content, targetText, cleaned);
        if (updated !== null) {
          finalHtml = updated;
        } else if (content.includes(targetText)) {
          // selection happens to be a literal HTML substring — safe plain replace
          finalHtml = content.split(targetText).join(cleaned);
        } else {
          ShowToast("Couldn't locate the selected text in the document — no changes were made. Please re-select and try again.", Severty.ERROR);
          return null;
        }
        setContent(finalHtml);
      } else {
        finalHtml = cleaned;
        setContent(cleaned);
        addChat("response", cleaned);
      }
      setAnalysis(null);
      ShowToast(isPartial ? "Selection updated!" : "Content updated!", Severty.SUCCESS);
      return finalHtml;
    } catch (e) {
      if (!e?.stopped && !isStale(runId)) ShowToast("Failed to apply action.", Severty.ERROR);
      return null;
    }
    finally { if (!isStale(runId)) setRewriteLoading(false); }
  };

  // ── Readability breakdown actions ─────────────────────────────────────
  // "Show in editor": select the flagged sentence in place so the user sees
  // exactly where the problem is (no document mutation).
  const locateSentence = (sentence) => {
    const found = selectTextInElement(document.querySelector(".wa-editor-surface"), sentence);
    if (!found) ShowToast("Couldn't find this sentence — it may have been edited already.", Severty.ERROR);
  };

  // "Fix this sentence": simplify ONLY that sentence in place, then refresh
  // the readability numbers so the breakdown stays on screen.
  const handleFixSentence = async (sentence, index) => {
    setFixingSentenceIndex(index);
    try {
      const updatedHtml = await handleAction("simplify", "", sentence);
      if (updatedHtml) await analyzeReadabilityLite(stripHtml(updatedHtml));
    } finally { setFixingSentenceIndex(null); }
  };

  const actionsMenu = {
    items: [
      { key: "rewrite",      label: "Rewrite",           icon: <ReloadOutlined /> },
      { key: "expand",       label: "Expand",            icon: <ExpandAltOutlined /> },
      { key: "condense",     label: "Condense",          icon: <CompressOutlined /> },
      { type: "divider" },
      { key: "improve",      label: "Improve Writing",   icon: <ThunderboltOutlined /> },
      { key: "simplify",     label: "Simplify",          icon: <FileTextOutlined /> },
      { key: "grammar",      label: "Fix Grammar",       icon: <CheckOutlined /> },
      { key: "professional", label: "Make Professional", icon: <RocketOutlined /> },
      { key: "engaging",     label: "Make Engaging",     icon: <SendOutlined /> },
      { type: "divider" },
      {
        key: "tone",
        label: "Tone",
        icon: <EditOutlined />,
        children: [
          { key: "tone-professional",  label: "Professional" },
          { key: "tone-friendly",      label: "Friendly" },
          { key: "tone-persuasive",    label: "Persuasive" },
          { key: "tone-casual",        label: "Casual" },
          { key: "tone-executive",     label: "Executive" },
          { key: "tone-inspirational", label: "Inspirational" },
          { type: "divider" },
          { key: "tone-custom",        label: "Custom..." },
        ],
      },
    ],
    onClick: ({ key }) => {
      if (key === "tone-custom") {
        setPendingSelection(getEditorSelection() || savedSelection);
        setCustomToneOpen(true);
      } else if (key.startsWith("tone-")) {
        handleAction("tone", key.slice("tone-".length));
      } else {
        handleAction(key);
      }
    },
  };

  const exportText = () => {
    if (!plainText.trim()) return;
    const blob = new Blob([plainText], { type: "text/plain" });
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = "writing-assistant-content.txt";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const handleAdd = async (actionType, postTitle = null) => {
    if (!plainText.trim()) { ShowToast("Content is empty.", Severty.ERROR); return; }
    setLoading(true);
    let apiUrl = "", dataToSend = null, method = "POST";
    if (actionType === "draft") {
      // Autosave may already have created this draft — revise that record
      // instead of leaving a duplicate behind.
      const draftId = getDraftId();
      apiUrl = draftId
        ? `/admin/access-module/writing-assistance/draft/${draftId}`
        : "/admin/access-module/writing-assistance/add";
      method = draftId ? "PUT" : "POST";
      dataToSend = { ...formData, title: formData.topic || formData.title, content, status: "draft", userId, chatData };
    } else if (actionType === "blog") {
      apiUrl = `/admin/access-module/addblog/${userId}`;
      dataToSend = { ...formData, title: formData.topic || formData.title, content, status: "draft", blogType: "manual" };
    } else {
      apiUrl = `/admin/access-module/calender/${userId}`;
      dataToSend = {
        ...formData,
        title: postTitle || formData.topic || "Untitled Post",
        postText: content,
        clientStatus: "Draft By Brand",
        brandStatus: "Draft",
        selectedDate: new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())).toISOString().replace("Z", "+00:00"),
      };
    }
    // `request` resolves only once the call finishes. The old code fired it
    // without awaiting and cleared `loading` on the very next line, so the
    // button's spinner never appeared and the action felt like a no-op.
    try {
      await request({
        url: apiUrl, method, data: dataToSend,
        onSuccess: (data) => {
          if (data.status === 200) {
            ShowToast(`${actionType} created successfully`, Severty.SUCCESS);
            // Keep autosave pointed at this record so later ticks revise it.
            if (actionType === "draft") setDraftId(data?.data?.blog?._id || getDraftId());
            setRefresh(Math.random());
          } else ShowToast(data.message, Severty.ERROR);
        },
        onError: () => ShowToast(`Error creating ${actionType}`, Severty.ERROR),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendForPost = () => {
    if (!plainText.trim()) { ShowToast("Content is empty.", Severty.ERROR); return; }
    if (onSendForPost) {
      onSendForPost(content);
    }
  };

  const loadSavedItem = (item) => {
    setSelectedItem(item);
    // Autosave must revise the draft being opened rather than fork a copy of it.
    setDraftId(item?._id || null);
    setContent(item?.content || "");
    // Older drafts saved before chat tracking existed (or saved by another
    // flow) have no chatData — synthesize one turn so the thread still shows
    // the content instead of falling back to the empty/welcome state.
    const chatData = item?.chatData?.length
      ? item.chatData
      : item?.content
      ? [{ type: "response", chatText: item.content, date: item.created_at || new Date().toISOString() }]
      : [];
    setChatData(chatData);
    setEditHistory([]);
    const values = { topic: item?.title || "", postDate: item?.postDate ? moment(item.postDate) : null };
    setFormData(values);
    form.setFieldsValue(values);
    setComposerText("");
    setComposerSettingsOpen(false);
  };

  const handleSelectChat = (history) => {
    const items = history.chatData || [];
    const lastResponse = [...items].reverse().find((item) => item.type === "response");
    setChatData(items);
    setEditHistory([]);
    setFormData(history.formData || {});
    setCurrentChatId(history.id);
    form.setFieldsValue(history.formData || {});
    setContent(lastResponse?.chatText || "");
    setComposerSettingsOpen(false);
  };

  const resetWorkspace = () => {
    form.resetFields();
    setContent("");
    setChatData([]);
    setEditHistory([]);
    setAnalysis(null);
    setFormData({});
    setCurrentChatId(null);
    setSelectedItem(null);
    setComposerText("");
    setComposerSettingsOpen(false);
    // Otherwise autosave would keep revising the draft that was just left,
    // instead of starting a fresh one for this new session.
    setDraftId(null);
  };

  const handleDeleteDraft = (item) => {
    request({
      url: `/admin/access-module/writing-assistance/draft/${item._id}?userId=${userId}`,
      method: "DELETE",
      onSuccess: (data) => {
        if (data.status === 200) {
          ShowToast("Draft deleted", Severty.SUCCESS);
          setSavedItems((prev) => prev.filter((d) => d._id !== item._id));
          // If the deleted draft is the one currently open, clear the editor —
          // its content no longer has a backing record.
          if (getDraftId() === item._id) resetWorkspace();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: () => ShowToast("Error deleting draft", Severty.ERROR),
    });
  };

  // ── Analysis colour helpers ────────────────────────────────────────────
  const charColor = charCount > 3000 ? "#ff4d4f" : charCount > 2700 ? "#fa8c16" : charCount > 1300 ? "#1890ff" : "#52c41a";
  const charLabel = charCount > 3000 ? "Over LinkedIn limit" : charCount > 2700 ? "Near limit" : charCount > 1300 ? "\"See more\" in feed" : charCount > 0 ? "Good" : "";
  const readColor = analysis?.readability?.score >= 7 ? "#52c41a" : analysis?.readability?.score >= 4 ? "#fa8c16" : "#ff4d4f";

  // Live accuracy derived from the CURRENT facts — stays in sync when a claim
  // is re-verified or fixed (the backend accuracy_score is analyze-time only)
  const accuracyScore = analysis?.facts?.length ? computeAccuracy(analysis.facts) : null;

  // Inline sentence highlighting — mirrors originality.ai's per-check
  // highlighting. Reuses the same buildHighlightedHtml engine for every mode —
  // only the {word/sentence, severity, tip} inputs change.
  const highlightHtml = useMemo(() => {
    if (!highlightMode || !analysis) return null;

    if (highlightMode === "readability") {
      const details = analysis.readability_details;
      if (!details) return null;
      const wordMap = {};
      (details.word_issues || []).forEach((w) => {
        wordMap[w.word] = { severity: w.severity, reason: w.reason };
      });
      const sentences = (details.sentence_issues || []).map((s) => ({
        text: s.sentence, severity: s.severity, tip: s.tip,
      }));
      if (!Object.keys(wordMap).length && !sentences.length) return null;
      return buildHighlightedHtml(content, wordMap, sentences);
    }

    if (highlightMode === "ai") {
      const blocks = analysis.ai_detection?.blocks || [];
      const sentences = blocks
        .filter((b) => b.fake_pct >= 50)
        .map((b) => ({
          text: b.text,
          severity: b.fake_pct >= 80 ? "high" : "medium",
          tip: `Likely AI (${b.fake_pct}% confidence)`,
        }));
      if (!sentences.length) return null;
      return buildHighlightedHtml(content, {}, sentences);
    }

    if (highlightMode === "plagiarism") {
      const matches = analysis.plagiarism?.matches || [];
      const sentences = matches
        .filter((m) => m.text)
        .map((m) => ({
          text: m.text,
          severity: m.similarity >= 80 ? "high" : "medium",
          tip: `${m.similarity}% match — ${m.url || "source unavailable"}`,
        }));
      if (!sentences.length) return null;
      return buildHighlightedHtml(content, {}, sentences);
    }

    if (highlightMode === "grammar") {
      const issues = analysis.grammar?.issues || [];
      const sentences = issues
        .filter((iss) => iss.sentence)
        .map((iss) => ({ text: iss.sentence, severity: "medium", tip: iss.message }));
      if (!sentences.length) return null;
      return buildHighlightedHtml(content, {}, sentences);
    }

    if (highlightMode === "facts") {
      const facts = analysis.facts || [];
      const sentences = facts
        .filter((f) => f.claim && f.verdict !== "Unverified")
        .map((f) => ({
          text: f.claim,
          severity: f.verdict === "Contradicted" ? "high" : f.verdict === "Partially Supported" ? "medium" : "low",
          tip: `${f.verdict}${f.confidence != null ? ` (${f.confidence}% confidence)` : ""}`,
        }));
      if (!sentences.length) return null;
      return buildHighlightedHtml(content, {}, sentences);
    }

    return null;
  }, [highlightMode, analysis, content]);

  // Which highlight modes actually have something to show — used to grey out
  // empty options in the selector rather than switching to a blank highlight.
  const highlightAvailability = {
    readability: !!analysis?.readability_details?.word_issues?.length || !!analysis?.readability_details?.sentence_issues?.length,
    ai: !!(analysis?.ai_detection?.blocks || []).some((b) => b.fake_pct >= 50),
    plagiarism: !!(analysis?.plagiarism?.matches || []).length,
    grammar: !!(analysis?.grammar?.issues || []).some((iss) => iss.sentence),
    facts: !!(analysis?.facts || []).some((f) => f.claim && f.verdict !== "Unverified"),
  };
  const hasHighlightData = Object.values(highlightAvailability).some(Boolean);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <div className="wa-root">

        {/* ── Page header ── */}
        <div className="wa-page-header">
          <div className="wa-page-header-left">
            <div className="wa-page-header-icon">
              <RocketOutlined />
            </div>
            <div>
              <h2 className="wa-page-title">Writing Assistant</h2>
              <p className="wa-page-subtitle">AI-powered content creation, analysis and publishing</p>
            </div>
          </div>
        </div>

        {/* ── Main two-column body ── */}
        <div className="wa-layout">

          <WritingChatSidebar
            chatHistories={chatHistories}
            onSelectChat={handleSelectChat}
            onDeleteChat={deleteChatHistory}
            onClearAll={clearAllChatHistories}
            onCreateNew={resetWorkspace}
            currentChatId={currentChatId}
            savedItems={savedItems}
            selectedItem={selectedItem}
            onSelectDraft={loadSavedItem}
            onDeleteDraft={handleDeleteDraft}
          />

          {/* Chat column */}
          <main className="wa-chat-column">

            {/* Toolbar */}
            <div className="wa-toolbar">
              <div className="wa-toolbar-left">
                <span className="wa-editor-label">Workspace</span>
                {isGenerating && (
                  <span className="wa-generating-badge">
                    <LoadingOutlined spin /> Generating
                  </span>
                )}
              </div>
              <div className="wa-toolbar-actions">
                <Tooltip title="Undo last change" placement="bottom">
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={handleUndo}
                    disabled={!editHistory.length || isGenerating || rewriteLoading}
                    className="wa-tool-btn wa-tool-btn-ghost"
                  />
                </Tooltip>

                <Tooltip title="Paste your own content to run the analyzer on it" placement="bottom">
                  <Button
                    size="small"
                    icon={<SnippetsOutlined />}
                    onClick={() => setPasteModalOpen(true)}
                    className="wa-tool-btn wa-tool-btn-ghost"
                  >
                    Analyze My Content
                  </Button>
                </Tooltip>

                <div className="wa-toolbar-divider" />

                <Dropdown menu={actionsMenu} trigger={["click"]} disabled={rewriteLoading}>
                  <Button size="small" className="wa-tool-btn wa-actions-btn" loading={rewriteLoading}>
                    Actions <DownOutlined style={{ fontSize: 10, marginLeft: 3 }} />
                  </Button>
                </Dropdown>

                <div className="wa-toolbar-divider" />

                <Tooltip title="Generate relevant & trending hashtags" placement="bottom">
                  <Button size="small" icon={<TagsOutlined />} loading={hashtagLoading} onClick={handleGenerateHashtags} className="wa-tool-btn wa-tool-btn-hashtag">
                    Hashtags
                  </Button>
                </Tooltip>

                {hasHighlightData && (
                  <>
                    <div className="wa-toolbar-divider" />
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        selectedKeys: highlightMode ? [highlightMode] : ["off"],
                        onClick: ({ key }) => setHighlightMode(key === "off" ? null : key),
                        items: [
                          { key: "off", label: "Off" },
                          { type: "divider" },
                          { key: "readability", label: "Readability", icon: <FileTextOutlined />, disabled: !highlightAvailability.readability },
                          { key: "ai", label: "AI Score", icon: <RobotOutlined />, disabled: !highlightAvailability.ai },
                          { key: "plagiarism", label: "Plagiarism", icon: <CopyOutlined />, disabled: !highlightAvailability.plagiarism },
                          { key: "grammar", label: "Grammar", icon: <CheckOutlined />, disabled: !highlightAvailability.grammar },
                          { key: "facts", label: "Fact Check", icon: <SearchOutlined />, disabled: !highlightAvailability.facts },
                        ],
                      }}
                    >
                      <Tooltip title="Highlight sentences flagged by a specific check, right in the editor" placement="bottom">
                        <Button size="small" type={highlightMode ? "primary" : "default"} icon={<HighlightOutlined />} className="wa-tool-btn">
                          Highlights{highlightMode ? `: ${highlightMode.charAt(0).toUpperCase() + highlightMode.slice(1)}` : ""}
                        </Button>
                      </Tooltip>
                    </Dropdown>
                  </>
                )}

                <div className="wa-toolbar-divider" />

                <Tooltip title="Download as .txt" placement="bottom">
                  <Button size="small" icon={<DownloadOutlined />} onClick={exportText} className="wa-tool-btn wa-tool-btn-ghost" />
                </Tooltip>
              </div>
            </div>

            {/* Hint */}
            <div className="wa-editor-hint">
              <span className="wa-hint-dot" /> Tip: Highlight any text in the latest message, then use Actions to apply Rewrite, Tone, or other edits to just that selection.
            </div>

            {/* Hashtag panel */}
            {hashtagPanelOpen && hashtags.length > 0 && (
              <div className="wa-hashtag-panel">
                <div className="wa-hashtag-panel-header">
                  <span className="wa-hashtag-panel-title">Suggested Hashtags</span>
                  <div className="wa-hashtag-panel-actions">
                    <button className="wa-hashtag-add-all" onClick={addAllHashtagsToContent}>Add All</button>
                    <button className="wa-hashtag-close" onClick={() => setHashtagPanelOpen(false)}>✕</button>
                  </div>
                </div>
                <div className="wa-hashtag-chips">
                  {hashtags.map((tag, i) => (
                    <button key={i} className={`wa-hashtag-chip${plainText.includes(tag) ? " wa-hashtag-chip-added" : ""}`} onClick={() => addHashtagToContent(tag)} title={plainText.includes(tag) ? "Already in post" : "Click to add"}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Conversation thread */}
            <div className="wa-chat-thread">
              {chatData.length === 0 ? (
                <div className="wa-chat-empty">
                  <div className="wa-chat-empty-icon"><RocketOutlined /></div>
                  <h3>Writing Assistant</h3>
                  <p>Describe a topic and I'll draft a LinkedIn post for you.</p>
                  <div className="wa-chat-starters">
                    {[
                      "Write a post about AI trends in 2026",
                      "Share a lesson learned from a recent project",
                      "Announce a new product launch",
                    ].map((s) => (
                      <button key={s} type="button" className="wa-chat-starter-chip" onClick={() => setComposerText(s)}>
                        {s}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="wa-chat-starter-chip"
                      onClick={() => setPasteModalOpen(true)}
                    >
                      <SnippetsOutlined style={{ marginRight: 6 }} />
                      Paste your own content to analyze
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {chatData.map((item, idx) => {
                    if (item.type === "prompt") {
                      return (
                        <div key={idx} className="wa-msg wa-msg-user">
                          <div className="wa-msg-bubble">{item.chatText}</div>
                        </div>
                      );
                    }
                    const isLive = idx === lastResponseIndex;
                    return (
                      <div key={idx} className="wa-msg wa-msg-assistant">
                        {isLive ? (
                          <div className="wa-msg-bubble wa-msg-card wa-editor-surface">
                            <DescriptionEditor value={content} onChange={setContent} highlightHtml={highlightHtml} />
                          </div>
                        ) : (
                          <div className="wa-msg-bubble wa-msg-card wa-msg-static" dangerouslySetInnerHTML={{ __html: item.chatText }} />
                        )}
                      </div>
                    );
                  })}
                  {isGenerating && chatData[chatData.length - 1]?.type === "prompt" && (
                    <div className="wa-msg wa-msg-assistant">
                      <div className="wa-msg-bubble wa-msg-card wa-msg-typing">
                        <LoadingOutlined spin /> Writing...
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Composer */}
            <div className="wa-composer">
              <div className="wa-composer-input-row">
                <div className="wa-settings-anchor" ref={settingsAnchorRef}>
                  <button
                    type="button"
                    className={`wa-composer-settings-toggle${composerSettingsOpen ? " active" : ""}`}
                    title="Language, length & keywords"
                    onClick={() => setComposerSettingsOpen((v) => !v)}
                  >
                    <SettingOutlined />
                  </button>

                  {composerSettingsOpen && (
                    <div className="wa-settings-panel">
                      <Form
                        form={form}
                        layout="vertical"
                        initialValues={{ language: "en", englishVariant: "us", wordCount: "300" }}
                      >
                        <Form.Item label="Language" name="language">
                          <Select>
                            <Select.Option value="en">English</Select.Option>
                            <Select.Option value="ar">Arabic</Select.Option>
                          </Select>
                        </Form.Item>

                        {(selectedLanguage || "en") === "en" && (
                          <Form.Item label="English Variant" name="englishVariant">
                            <Select>
                              <Select.Option value="us">US</Select.Option>
                              <Select.Option value="uk">UK</Select.Option>
                            </Select>
                          </Form.Item>
                        )}

                        <Form.Item
                          label="Word Length"
                          name="wordCount"
                          style={{ marginBottom: selectedLength === "custom" ? 8 : 16 }}
                        >
                          <Radio.Group buttonStyle="solid" style={{ display: "flex", width: "100%" }}>
                            {[
                              { value: "150",    label: "Short",    sub: "150w"  },
                              { value: "300",    label: "Standard", sub: "300w"  },
                              { value: "500",    label: "Long",     sub: "500w"  },
                              { value: "1000",   label: "Article",  sub: "1000w" },
                              { value: "custom", label: "Custom",   sub: "···"   },
                            ].map(({ value, label, sub }) => (
                              <Radio.Button key={value} value={value} style={{ flex: 1, textAlign: "center", height: "auto", padding: "6px 2px" }}>
                                <div style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{label}</div>
                                <div style={{ fontSize: 10, opacity: 0.7, lineHeight: 1.3 }}>{sub}</div>
                              </Radio.Button>
                            ))}
                          </Radio.Group>
                        </Form.Item>

                        {selectedLength === "custom" && (
                          <Form.Item name="customWordCount" rules={[{ required: true, message: "Enter word count" }]}>
                            <Input
                              type="number"
                              min={50}
                              max={3000}
                              placeholder="Enter word count (e.g. 600)"
                            />
                          </Form.Item>
                        )}

                        <Form.Item label="Keywords" name="keywords" style={{ marginBottom: 0 }}>
                          <Input placeholder="AI, leadership, growth..." />
                        </Form.Item>
                      </Form>
                    </div>
                  )}
                </div>
                <Input.TextArea
                  className="wa-composer-textarea"
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  value={composerText}
                  onChange={(e) => setComposerText(e.target.value)}
                  placeholder={content ? 'Ask for a change — e.g. "make it shorter"' : "E.g., Write a conversational post about AI trends in 2025..."}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      submitComposer();
                    }
                  }}
                />
                {isGenerating ? (
                  <button type="button" className="wa-stop-icon wa-composer-stop" onClick={handleStopProcess} aria-label="Stop" title="Stop">
                    <svg className="wa-stop-ring" viewBox="0 0 36 36" aria-hidden="true">
                      <circle cx="18" cy="18" r="15" />
                    </svg>
                    <span className="wa-stop-core" />
                  </button>
                ) : (
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<SendOutlined />}
                    onClick={submitComposer}
                    disabled={!composerText.trim()}
                    className="wa-composer-send"
                  />
                )}
              </div>
            </div>

            {/* Current draft actions */}
            {content && (
              <div className="wa-draft-actions">
                <div className="wa-footer-stats">
                  <span className="wa-stat">{wordCount} <small>words</small></span>
                  <span className="wa-stat-sep">·</span>
                  <span className="wa-stat">{readingTime} <small>min</small></span>
                  {charCount > 0 && (
                    <>
                      <span className="wa-stat-sep">·</span>
                      <Tooltip title={charLabel} placement="top">
                        <span className="wa-stat wa-char-count" style={{ color: charColor }}>
                          {charCount.toLocaleString()} <small>/ 3,000</small>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </div>
                <div className="wa-footer-actions">
                  <Button onClick={resetWorkspace} size="small" className="wa-action-ghost">New</Button>
                  <Button onClick={() => handleAdd("draft")} loading={loading} size="small">Save Draft</Button>
                  <Button onClick={() => handleAdd("blog")} size="small" icon={<FileTextOutlined />}>Send to Blog</Button>
                  <Button onClick={handleSendForPost} type="primary" size="small" icon={<SendOutlined />} className="wa-btn-send-post">
                    Send For Post
                  </Button>
                </div>
              </div>
            )}
          </main>
        </div>

        {/* ── Analysis section ── */}
        <section className="wa-analysis">
          <div className="wa-analysis-header">
            <div className="wa-analysis-title-group">
              <h3 className="wa-analysis-title">Content Intelligence</h3>
              <p className="wa-analysis-subtitle">
                {analysis
                  ? "Analysis complete — review your scores below"
                  : "Run analysis to get readability score, writing suggestions and fact check"}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Button
                type="primary"
                loading={analysisLoading}
                onClick={handleAnalyze}
                icon={<ThunderboltOutlined />}
                className="wa-analyze-btn"
              >
                {analysisLoading ? "Analyzing..." : "Analyze Content"}
              </Button>
              {analysisLoading && (
                <button type="button" className="wa-stop-icon" onClick={handleStopProcess} aria-label="Stop" title="Stop">
                  <svg className="wa-stop-ring" viewBox="0 0 36 36" aria-hidden="true">
                    <circle cx="18" cy="18" r="15" />
                  </svg>
                  <span className="wa-stop-core" />
                </button>
              )}
            </div>
          </div>

          {analysis?.linkedin && (
            <div className="wa-linkedin-checks">
              {[
                { label: "Hook", ok: analysis.linkedin.hook, pass: "Strong", fail: "Weak opener" },
                { label: "CTA", ok: analysis.linkedin.cta, pass: "Present", fail: "Missing" },
                {
                  label: "Length",
                  ok: analysis.linkedin.char_status !== "Over Limit",
                  pass: `${analysis.linkedin.char_count?.toLocaleString()} chars · ${analysis.linkedin.char_status}`,
                  fail: `${analysis.linkedin.char_count?.toLocaleString()} chars · Over Limit`,
                },
                {
                  label: "Hashtags",
                  ok: analysis.linkedin.hashtag_status !== "Too Many",
                  pass: `${analysis.linkedin.hashtag_count} · ${analysis.linkedin.hashtag_status}`,
                  fail: `${analysis.linkedin.hashtag_count} · Too Many`,
                },
              ].map(({ label, ok, pass, fail }) => (
                <div key={label} className={`wa-check-chip ${ok ? "wa-check-pass" : "wa-check-fail"}`}>
                  <span className="wa-check-dot">{ok ? "✓" : "✗"}</span>
                  <span className="wa-check-label">{label}</span>
                  <span className="wa-check-value">{ok ? pass : fail}</span>
                </div>
              ))}
            </div>
          )}

          <Row gutter={[16, 16]}>
            {/* Readability */}
            <Col xs={24} lg={8}>
              <div className="wa-metric-card">
                <div className="wa-metric-card-top">
                  <div className="wa-metric-icon" style={{ background: analysis?.readability?.score != null ? `${readColor}1a` : "#f1f5f9" }}>
                    <FileTextOutlined style={{ color: analysis?.readability?.score != null ? readColor : "#94a3b8" }} />
                  </div>
                  <div className="wa-metric-label">Readability Score</div>
                </div>

                {/* Overall score — average of Flesch-Kincaid Reading Ease and
                    Dale-Chall Readability Grade, each normalised 0-100 against
                    its own target band (so a 1-point Dale-Chall move and a
                    1-point Flesch move aren't weighted the same). Target: 80+. */}
                {analysis?.readability_details?.overall_score != null && (() => {
                  const score = analysis.readability_details.overall_score;
                  const color = score >= 80 ? "#16a34a" : score >= 50 ? "#d97706" : "#dc2626";
                  const status = score >= 80 ? "Excellent" : score >= 50 ? "Needs Improvement" : "Poor";
                  return (
                    <div style={{ textAlign: "center", margin: "4px 0 10px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, textTransform: "uppercase" }}>
                        Overall Score
                      </div>
                      <div style={{ margin: "2px 0" }}>
                        <span style={{ fontSize: 34, fontWeight: 800, color }}>{score}</span>
                        <span style={{ fontSize: 16, color: "#94a3b8" }}>/100</span>
                      </div>
                      <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden", maxWidth: 220, margin: "6px auto 0", position: "relative" }}>
                        <div style={{ width: `${score}%`, height: "100%", background: color, borderRadius: 3 }} />
                        <div style={{ position: "absolute", top: 0, left: "80%", width: 2, height: "100%", background: "#0f172a4d" }} />
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color, marginTop: 4 }}>{status}</div>
                    </div>
                  );
                })()}
                {/* originality.ai-style half-circle gauges — only the original 4
                    formulas here; the rest live in Readability Metrics below */}
                {analysis?.readability_details?.metrics?.length > 0 && (
                  <Row gutter={[8, 10]} style={{ marginTop: 8 }}>
                    {analysis.readability_details.metrics
                      .filter((m) => PRIMARY_READABILITY_KEYS.includes(m.key))
                      .map((m) => (
                        <Col span={12} key={m.key}>
                          <ReadabilityGauge metric={m} />
                        </Col>
                      ))}
                  </Row>
                )}

                {analysis?.readability_details?.overall_score != null && analysis.readability_details.overall_score < 80 && (
                  <button
                    className="wa-fix-content-btn"
                    style={{ marginTop: 12, width: "100%" }}
                    disabled={rewriteLoading}
                    onClick={handleImproveReadability}
                  >
                    {rewriteLoading ? "Improving..." : "✦ Improve Readability"}
                  </button>
                )}
                <div className="wa-metric-card-fill" />
              </div>
            </Col>

            {/* Guideline Checker — our own LLM-based feature. originality.ai's
                version isn't reachable via their API at all (confirmed against
                their published /scan parameter list). */}
            <Col xs={24} lg={8}>
              <div className="wa-metric-card wa-metric-card-list">
                <div className="wa-metric-list-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="wa-metric-icon wa-metric-icon-sm" style={{ background: "#eff6ff" }}>
                      <CheckOutlined style={{ color: "#2563eb" }} />
                    </div>
                    <div className="wa-metric-label">Guideline Checker</div>
                  </div>
                  {guidelineCheck && (
                    <button className="wa-fact-retry-btn" onClick={handleCheckGuidelines} disabled={guidelineCheckLoading} title="Re-check">
                      ↻
                    </button>
                  )}
                </div>

                {!guidelineCheck && !guidelineCheckLoading && (
                  <>
                    <p className="wa-metric-empty">Checks your post against AP-inspired style conventions — punctuation, number formatting, active voice.</p>
                    <button className="wa-fix-content-btn" style={{ width: "100%", marginTop: 8 }} onClick={handleCheckGuidelines}>
                      Check Guidelines
                    </button>
                  </>
                )}

                {guidelineCheckLoading && (
                  <p className="wa-metric-empty" style={{ textAlign: "center" }}><LoadingOutlined spin style={{ marginRight: 6 }} />Checking guidelines...</p>
                )}

                {guidelineCheck && !guidelineCheckLoading && (() => {
                  const score = guidelineCheck.alignment_score;
                  const label = score == null ? null : score >= 90 ? "Highly Aligned" : score >= 70 ? "Mostly Aligned" : score >= 40 ? "Needs Revision" : "Poorly Aligned";
                  const readiness = score == null ? null : score >= 80 ? "Ready for Final Human Review" : "Needs Editor Review";
                  const color = score == null ? "#64748b" : score >= 80 ? "#16a34a" : score >= 40 ? "#d97706" : "#dc2626";
                  return (
                    <>
                      {score != null && (
                        <>
                          <PercentGauge percent={score} colorGood={true} />
                          <p style={{ textAlign: "center", fontWeight: 700, fontSize: 14, color, margin: "4px 0 2px" }}>
                            {score}% - {label}
                          </p>
                          {readiness && <p style={{ textAlign: "center", fontWeight: 600, fontSize: 12, color, margin: "0 0 8px" }}>{readiness}</p>}
                          <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                            <span style={{ background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6 }}>
                              Guideline: {guidelineCheck.guideline || "AP-inspired"}
                            </span>
                            <span style={{ background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6 }}>
                              {moment().format("MMM D, YYYY")}
                            </span>
                          </div>
                        </>
                      )}

                      {guidelineCheck.summary && (
                        <div className="wa-fact-explanation" style={{ marginBottom: 10 }}>
                          <strong>Summary</strong>
                          <p>{guidelineCheck.summary}</p>
                        </div>
                      )}

                      {(guidelineCheck.sentences || []).length > 0 ? (
                        <>
                          <div className="wa-metric-label" style={{ marginBottom: 8 }}>Detailed Explanations</div>
                          <div className="wa-fact-checks">
                            {guidelineCheck.sentences.map((s, i) => (
                              <div key={i} className="wa-fact-item">
                                <div className="wa-fact-claim wa-fact-claim-false">{s.text}</div>
                                <div className="wa-fact-badge" style={{ background: "#fee2e218", color: "#dc2626", border: "1px solid #fca5a533" }}>
                                  Poorly Aligned
                                </div>
                                {s.explanation && (
                                  <div className="wa-fact-explanation">
                                    <strong>Explanation</strong>
                                    <p>{s.explanation}</p>
                                  </div>
                                )}
                                {s.suggestion && (
                                  <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: "10px 12px", marginTop: 8 }}>
                                    <strong style={{ fontSize: 12, color: "#15803d" }}>Suggested Change</strong>
                                    <p style={{ fontSize: 13, color: "#166534", margin: "4px 0 8px" }}>{s.suggestion}</p>
                                    <button className="wa-fact-retry-btn" onClick={() => handleCopyGuidelineSuggestion(s.suggestion)}>
                                      Copy Suggestion
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        score != null && <p className="wa-metric-empty">No guideline violations found.</p>
                      )}

                      <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
                        This score checks the guideline rules we evaluate — it isn't an official style-manual assessment.
                      </p>
                    </>
                  );
                })()}
              </div>
            </Col>

            {/* Fact check — Originality.ai style */}
            <Col xs={24} lg={8}>
              <div className="wa-metric-card wa-metric-card-list">
                <div className="wa-metric-list-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="wa-metric-icon wa-metric-icon-sm" style={{ background: "#fef3c7" }}>
                      <SearchOutlined style={{ color: "#d97706" }} />
                    </div>
                    <div className="wa-metric-label">Fact Check</div>
                  </div>
                  {(analysis?.facts || []).some((f) => (f.sources || []).length > 0) && (
                    <button className="wa-fact-retry-btn" onClick={handleExportCitations} title="Export Citations">
                      ⬇
                    </button>
                  )}
                </div>
                {accuracyScore != null && (
                  <>
                    <PercentGauge percent={accuracyScore} colorGood={true} />
                    <p style={{ textAlign: "center", fontWeight: 700, fontSize: 14, color: accuracyScore >= 70 ? "#16a34a" : accuracyScore >= 40 ? "#d97706" : "#dc2626", margin: "4px 0 6px" }}>
                      {accuracyScore}% Of Claims Are Verified True
                    </p>
                    <p className="wa-metric-empty" style={{ textAlign: "center", marginBottom: 10 }}>
                      Share of detected claims confirmed accurate by web sources — not a confidence score.
                    </p>
                    {accuracyScore < 90 && (
                      <div style={{ textAlign: "center", marginBottom: 10 }}>
                        <button
                          className="wa-fix-content-btn"
                          onClick={handleFixContent}
                          disabled={rewriteLoading}
                        >
                          {rewriteLoading ? "Fixing..." : "Fix & Regenerate"}
                        </button>
                      </div>
                    )}
                  </>
                )}
                {analysis && !(analysis?.facts || []).length && (
                  <p className="wa-metric-empty" style={{ color: "#64748b", fontSize: 13 }}>
                    No verifiable claims found. This content is primarily opinion or reflection — nothing to fact-check.
                  </p>
                )}
                {(analysis?.facts || []).length ? (
                  <div className="wa-fact-checks">
                    {analysis.facts.map((f, i) => {
                      const v = f.verdict || "Unverified";
                      const isFalse   = v === "Contradicted";
                      const isPartial = v === "Partially Supported";
                      const isTrue    = v === "Supported";
                      const clsMap    = { Contradicted: "wa-fact-claim-false", "Partially Supported": "wa-fact-claim-partial", Supported: "wa-fact-claim-true" };
                      const claimCls  = clsMap[v] || "wa-fact-claim-unverified";
                      const badgeColor = isFalse ? "#dc2626" : isPartial ? "#d97706" : isTrue ? "#16a34a" : "#6b7280";
                      const badgeText = isFalse
                        ? `${f.confidence || 0}% Potentially False`
                        : isTrue
                        ? `${f.confidence || 0}% Supported`
                        : isPartial
                        ? `${f.confidence || 0}% Partially Supported`
                        : "Unverified";
                      const isRetrying = retryingIndex === i;
                      const isEditing  = editingClaim.index === i;
                      return (
                        <div key={i} className="wa-fact-item">
                          <div className={`wa-fact-claim ${claimCls}`}>{f.claim}</div>
                          <div className="wa-fact-badge" style={{ background: `${badgeColor}18`, color: badgeColor, border: `1px solid ${badgeColor}33` }}>
                            {isRetrying ? <><LoadingOutlined spin style={{ marginRight: 5 }} />Verifying...</> : badgeText}
                          </div>
                          {f.explanation && (
                            <div className="wa-fact-explanation">
                              <strong>Explanation</strong>
                              <p>{f.explanation}</p>
                            </div>
                          )}
                          {(f.sources || []).length > 0 && (
                            <div className="wa-fact-sources">
                              <strong style={{ fontSize: 12, color: "#334155" }}>Sources ({f.sources.length})</strong>
                              {f.sources.map((s, j) => (
                                <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="wa-fact-source-link" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {s.title || s.url}
                                  </a>
                                  {s.date && <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>{moment(s.date).isValid() ? moment(s.date).format("MMM D, YYYY") : s.date}</span>}
                                  <button
                                    type="button"
                                    className="wa-fact-retry-btn"
                                    style={{ padding: "1px 8px", fontSize: 11 }}
                                    onClick={async () => {
                                      try {
                                        await navigator.clipboard.writeText(formatCitationAPA(s));
                                        ShowToast("Citation copied!", Severty.SUCCESS);
                                      } catch {
                                        ShowToast("Could not copy — clipboard access blocked.", Severty.ERROR);
                                      }
                                    }}
                                  >
                                    Citation
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {f.rewrite && (
                            <div className="wa-fact-explanation" style={{ marginTop: 8 }}>
                              <strong>Corrected version</strong>
                              <p>{f.rewrite}</p>
                              <button
                                type="button"
                                className="wa-fact-retry-btn"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(f.rewrite);
                                    ShowToast("Copied!", Severty.SUCCESS);
                                  } catch {
                                    ShowToast("Could not copy — clipboard access blocked.", Severty.ERROR);
                                  }
                                }}
                              >
                                Copy
                              </button>
                            </div>
                          )}
                          {v !== "Supported" && !isRetrying && !isEditing && (
                            <div className="wa-fact-actions-row">
                              <button className="wa-fact-retry-btn" onClick={() => setEditingClaim({ index: i, text: f.claim })}>
                                ✏ Edit & Retry
                              </button>
                              <button
                                className="wa-fact-fix-btn"
                                onClick={() => handleFixClaim(i)}
                                disabled={fixingClaimIndex === i}
                              >
                                {fixingClaimIndex === i
                                  ? <><LoadingOutlined spin style={{ marginRight: 5 }} />Fixing...</>
                                  : <>⚡ Fix & Regenerate</>}
                              </button>
                            </div>
                          )}
                          {isEditing && (
                            <div className="wa-fact-edit">
                              <input className="wa-fact-edit-input" value={editingClaim.text} onChange={(e) => setEditingClaim((p) => ({ ...p, text: e.target.value }))} placeholder="Rephrase as a complete sentence..." autoFocus />
                              <div className="wa-fact-edit-actions">
                                <button className="wa-fact-verify-btn" onClick={handleRetryVerify}>Verify</button>
                                <button className="wa-fact-cancel-btn" onClick={() => setEditingClaim({ index: null, text: "" })}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  !analysis && <p className="wa-metric-empty">Run analysis to fact-check your claims.</p>
                )}
              </div>
            </Col>
          </Row>

          {/* Plagiarism + AI Detection + Grammar & Spelling + Content Quality — all
              powered by originality.ai, one row, each falls back independently
              when the API key is missing or the call fails. */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <div className="wa-metric-card wa-metric-card-list">
                <div className="wa-metric-list-header">
                  <div className="wa-metric-icon wa-metric-icon-sm" style={{ background: "#ffe4e6" }}>
                    <CopyOutlined style={{ color: "#be123c" }} />
                  </div>
                  <div className="wa-metric-label">Plagiarism</div>
                </div>
                {analysis?.plagiarism?.plagiarism_score != null ? (
                  <>
                    <PercentGauge percent={100 - analysis.plagiarism.plagiarism_score} colorGood={true} />
                    <p style={{ textAlign: "center", fontWeight: 700, fontSize: 14, color: analysis.plagiarism.plagiarism_score <= 10 ? "#16a34a" : analysis.plagiarism.plagiarism_score <= 30 ? "#d97706" : "#dc2626", margin: "4px 0 10px" }}>
                      {Math.round(100 - analysis.plagiarism.plagiarism_score)}% Original Content
                    </p>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                      <span style={{ background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6 }}>
                        Checked: {moment().format("MMM D, YYYY")}
                      </span>
                    </div>
                    <div className="wa-metric-label" style={{ marginBottom: 8 }}>
                      Matched Phrases ({(analysis.plagiarism.matches || []).length})
                    </div>
                    {(analysis.plagiarism.matches || []).length ? (
                      <div className="wa-fact-checks">
                        {analysis.plagiarism.matches.slice(0, 8).map((m, i) => (
                          <div key={i} className="wa-fact-item">
                            <div className="wa-fact-badge" style={{ background: "#be123c18", color: "#be123c", border: "1px solid #be123c33" }}>
                              {m.similarity}% match
                            </div>
                            {m.text && <div className="wa-fact-explanation"><p>"{m.text}"</p></div>}
                            {(m.sources || []).length > 0 ? (
                              <div className="wa-fact-sources">
                                {m.sources.map((src, j) => (
                                  <a key={j} href={src.url} target="_blank" rel="noopener noreferrer" className="wa-fact-source-link">
                                    {src.title || src.url} ({src.similarity}%)
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <a href={m.url} target="_blank" rel="noopener noreferrer" className="wa-fact-source-link">{m.url}</a>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="wa-metric-empty">No matched phrases found.</p>
                    )}
                  </>
                ) : (
                  <p className="wa-metric-empty">
                    {analysis
                      ? "Plagiarism check unavailable — showing fallback analysis only."
                      : "Run analysis to scan for plagiarism."}
                  </p>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <div className="wa-metric-card wa-metric-card-list">
                <div className="wa-metric-list-header">
                  <div className="wa-metric-icon wa-metric-icon-sm" style={{ background: "#ede9fe" }}>
                    <RobotOutlined style={{ color: "#6d28d9" }} />
                  </div>
                  <div className="wa-metric-label">AI Detection</div>
                </div>
                {analysis?.ai_detection?.ai_score != null ? (
                  <>
                    <PercentGauge percent={analysis.ai_detection.ai_score} colorGood={false} />
                    <p style={{ textAlign: "center", fontWeight: 700, fontSize: 14, color: analysis.ai_detection.ai_score >= 50 ? "#dc2626" : "#16a34a", margin: "4px 0 6px" }}>
                      {analysis.ai_detection.verdict} · {Math.round(analysis.ai_detection.ai_score)}% Confident
                    </p>
                    <p className="wa-metric-empty" style={{ textAlign: "center" }}>
                      This score reflects how confident the model is that the text is AI-written — it isn't a measure of what fraction of the text is AI-generated.
                    </p>
                    <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                      {analysis.ai_detection.model && (
                        <span style={{ background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6 }}>
                          Model used: {analysis.ai_detection.model}
                        </span>
                      )}
                      <span style={{ background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6 }}>
                        {moment().format("MMM D, YYYY")}
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="wa-metric-empty">
                    {analysis
                      ? "AI detection unavailable — showing fallback analysis only."
                      : "Run analysis to check for AI-generated content."}
                  </p>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <div className="wa-metric-card wa-metric-card-list">
                <div className="wa-metric-list-header">
                  <div className="wa-metric-icon wa-metric-icon-sm" style={{ background: "#dcfce7" }}>
                    <CheckOutlined style={{ color: "#16a34a" }} />
                  </div>
                  <div className="wa-metric-label">Grammar & Spelling</div>
                </div>
                {analysis?.grammar?.score != null ? (() => {
                  const issues = analysis.grammar.issues || [];
                  const spellingIssues = issues.filter((iss) => /typo|spelling/i.test(iss.category || ""));
                  const grammarIssues = issues.filter((iss) => !/typo|spelling/i.test(iss.category || ""));
                  return (
                    <>
                      <PercentGauge percent={analysis.grammar.score} colorGood={true} />
                      <p style={{ textAlign: "center", fontWeight: 700, fontSize: 14, color: issues.length === 0 ? "#16a34a" : "#92400e", margin: "4px 0 10px" }}>
                        {Math.round(analysis.grammar.score)}% {issues.length === 0 ? "Correct" : "Grammar Score"}
                      </p>
                      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                        <span style={{ background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6 }}>
                          {issues.length} {issues.length === 1 ? "issue found" : "issues found"}
                        </span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: (grammarIssues.length || spellingIssues.length) ? 10 : 0 }}>
                        <div style={{ background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 6 }}>
                          {grammarIssues.length === 0 ? "No grammar issues" : `${grammarIssues.length} grammar issues`}
                        </div>
                        <div style={{ background: "#fef2f2", color: "#dc2626", fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 6 }}>
                          {spellingIssues.length === 0 ? "No spelling issues" : `${spellingIssues.length} spelling issues`}
                        </div>
                      </div>
                      {issues.length > 0 && (
                        <div className="wa-fact-checks">
                          {issues.slice(0, 4).map((iss, i) => (
                            <div key={i} className="wa-fact-item">
                              <div className="wa-fact-badge" style={{ background: "#fef3c718", color: "#b45309", border: "1px solid #fde68a" }}>
                                {iss.category || "Issue"}
                              </div>
                              <div className="wa-fact-claim wa-fact-claim-partial">{iss.message}</div>
                              {(iss.replacements || []).length > 0 && (
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                                  Suggested: {iss.replacements.join(", ")}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <p className="wa-metric-empty">
                    {analysis
                      ? "Grammar check unavailable — showing fallback analysis only."
                      : "Run analysis to check grammar and spelling."}
                  </p>
                )}
              </div>
            </Col>

            {/* Content Quality — originality.ai's High/Medium/Low content-quality classifier */}
            <Col xs={24} sm={12} lg={6}>
              <div className="wa-metric-card wa-metric-card-list">
                <div className="wa-metric-list-header">
                  <div className="wa-metric-icon wa-metric-icon-sm" style={{ background: "#e0f2fe" }}>
                    <FileTextOutlined style={{ color: "#0284c7" }} />
                  </div>
                  <div className="wa-metric-label">Content Quality</div>
                </div>
                {analysis?.content_quality?.label ? (
                  <>
                    <div
                      className="wa-accuracy-banner"
                      style={{
                        background: analysis.content_quality.label === "High" ? "#dcfce7" : analysis.content_quality.label === "Medium" ? "#fef3c7" : "#fee2e2",
                        color: analysis.content_quality.label === "High" ? "#15803d" : analysis.content_quality.label === "Medium" ? "#92400e" : "#991b1b",
                        borderColor: analysis.content_quality.label === "High" ? "#86efac" : analysis.content_quality.label === "Medium" ? "#fcd34d" : "#fca5a5",
                      }}
                    >
                      <span className="wa-accuracy-pct">
                        {analysis.content_quality.label === "High" ? "✓" : analysis.content_quality.label === "Medium" ? "⚠" : "✗"}
                        &nbsp;{analysis.content_quality.label}
                        {analysis.content_quality.confidence != null ? ` · ${analysis.content_quality.confidence}%` : ""}
                      </span>
                    </div>
                    {Object.keys(analysis.content_quality.probs || {}).length > 0 && (
                      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                        {["High", "Medium", "Low"].filter((band) => analysis.content_quality.probs[band] != null).map((band) => {
                          const val = analysis.content_quality.probs[band];
                          const color = band === "High" ? "#16a34a" : band === "Medium" ? "#d97706" : "#dc2626";
                          return (
                            <div key={band}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}>
                                <span>{band}</span><span>{val}%</span>
                              </div>
                              <div style={{ background: "#f1f5f9", borderRadius: 4, height: 6, overflow: "hidden" }}>
                                <div style={{ width: `${val}%`, background: color, height: "100%" }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="wa-metric-empty">
                    {analysis
                      ? "Content quality check unavailable — showing fallback analysis only."
                      : "Run analysis to check content quality."}
                  </p>
                )}
              </div>
            </Col>
          </Row>

          {/* ── Readability breakdown — shows WHY the score is what it is ── */}
          {analysis?.readability_details?.metrics?.length > 0 && (
            <div className="wa-metric-card" style={{ marginTop: 16 }}>
              <div className="wa-metric-list-header" style={{ flexWrap: "wrap", gap: 8 }}>
                <div className="wa-metric-icon wa-metric-icon-sm" style={{ background: "#f0fdf4" }}>
                  <FileTextOutlined style={{ color: "#16a34a" }} />
                </div>
                <div className="wa-metric-label">Readability Metrics</div>
              </div>

              {/* Expandable metric list (originality.ai-style) */}
              <div style={{ marginTop: 12 }}>
                <ReadabilityMetricList metrics={analysis.readability_details.metrics.filter((m) => PRIMARY_READABILITY_KEYS.includes(m.key))} />
              </div>

              {/* Text Statistics — mirrors originality.ai's panel */}
              {analysis.readability_details.stats?.words != null && (
                <div style={{ marginTop: 16 }}>
                  <div className="wa-metric-label" style={{ marginBottom: 8 }}>Text Statistics</div>
                  <Row gutter={[10, 10]}>
                    {[
                      { label: "Word Count",        value: analysis.readability_details.stats.words },
                      { label: "Letter Count",      value: analysis.readability_details.stats.letters },
                      { label: "Sentence Count",    value: analysis.readability_details.stats.sentences },
                      { label: "Unique Word Count", value: analysis.readability_details.stats.unique_words },
                      { label: "Syllable Count",    value: analysis.readability_details.stats.syllables },
                      { label: "Words / Sentence",  value: analysis.readability_details.stats.avg_sentence_len },
                    ].filter((s) => s.value != null).map((s) => (
                      <Col xs={12} sm={8} md={8} lg={4} key={s.label}>
                        <div style={{ background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 8, padding: "8px 10px" }}>
                          <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{s.value}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{s.label}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

            </div>
          )}
        </section>
      </div>

      {/* ── Paste-your-own-content modal ── */}
      <Modal
        className="wa-paste-modal"
        title={
          <span className="wa-modal-title">
            <SnippetsOutlined style={{ color: "#0b7bea" }} />
            Analyze My Content
          </span>
        }
        open={pasteModalOpen}
        onCancel={() => { setPasteModalOpen(false); setPasteText(""); }}
        onOk={handleLoadOwnContent}
        okText="Load Content"
        cancelText="Cancel"
        width={560}
      >
        <p className="wa-paste-modal-desc">
          Paste text you've already written — it loads into the workspace so you can run Analyze Content (readability, AI detection, plagiarism, grammar, fact-check) on it, same as any generated draft.
        </p>
        <Input.TextArea
          className="wa-paste-modal-textarea"
          placeholder="Paste your content here..."
          rows={8}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          autoFocus
        />
      </Modal>

      {/* ── Custom Tone modal ── */}
      <Modal
        title={<><EditOutlined style={{ color: "#0b7bea", marginRight: 8 }} />Custom Tone</>}
        open={customToneOpen}
        onCancel={() => { setCustomToneOpen(false); setCustomToneValue(""); setPendingSelection(""); }}
        onOk={() => {
          if (!customToneValue.trim()) { ShowToast("Please describe the tone.", Severty.ERROR); return; }
          const sel = pendingSelection;
          setCustomToneOpen(false);
          const tone = customToneValue.trim();
          setCustomToneValue("");
          setPendingSelection("");
          handleAction("tone", tone, sel);
        }}
        okText="Apply Tone"
        cancelText="Cancel"
        width={480}
      >
        <p style={{ marginBottom: 12, color: "#64748b", fontSize: 13 }}>
          Describe the tone you want:
        </p>
        <Input.TextArea
          placeholder='e.g. "casual and witty", "formal academic", "bold and direct", "empathetic and warm"'
          rows={3}
          value={customToneValue}
          onChange={(e) => setCustomToneValue(e.target.value)}
          autoFocus
        />
      </Modal>
    </>
  );
};

export default WritingIndex;
